"use strict";
/**
 * 行业分类表 —— AI 建站时「你属于什么行业」那个选择器的真源。
 *
 * ## 一级的骨架照抄阿里巴巴国际站
 *
 * 38 个一级、它们的英文名与顺序，对齐 alibaba.com 分类导航（2026-11 抓取）。
 * 不是因为我们认同它的分类学，而是**客户认得它**：一个做光伏支架的工厂
 * 在阿里上被归进 Renewable Energy，他打开我们的选择器看到同一个词，
 * 不需要理解我们内部的行业划分。替它另发明一套 8 个行业的结果是
 * 「光伏只能塞进电子与电气」，谁都觉得不对。
 *
 * 照抄的代价写在下面「为什么二级是自己写的」里：**只抄一级，不抄它的树**。
 * 阿里一级有 38 个，叶子有 5900 个，那是买家找货的检索结构；我们要的是
 * 工厂描述自己的口径，所以二级按「这个品类的工厂会怎么分自己的产品线」重写，
 * 每个 4–6 个、每个 8 条真实商品名。
 *
 * 阿里会改它的分类（不同地区版本就多出 Environment / Business Services），
 * 所以这份表**不是它的镜像**：改的时候以客户能不能找到自己为准，不以对齐为准。
 *
 * ## 为什么分两级，而不是平铺一张长列表
 *
 * 一级回答「我是做什么的」（38 选 1 + 搜索框），二级回答「具体卖什么」
 * （4–6 选 1）。平铺 228 个选项没人扫得动，而他脑子里先有的是"我是五金厂"。
 *
 * 更重要的是**两级绑的是不同的东西**：
 *
 *   · 一级 → 版式 + 配色 + 文案语气（变化极慢，你后期手工维护）
 *   · 二级 → 商品示例名 + 配图提示词（会一直往上加）
 *
 * 混成一级的话，以后加一个二级品类要顺带想"它该配哪套版式"，
 * 而这件事其实早被它的一级决定了。
 *
 * ## 为什么 seeds 是中英成对，而不是两套数组
 *
 * 它有两个消费点，要的语言不一样：
 *   · 拼提示词时要英文（`Fiberglass Claw Hammer`）—— 模型据此知道这个品类长什么样
 *   · 追问时当可点的 chip 要中文（`羊角锤`）—— 用户在对话框里点两下就答完
 *
 * 分成两个平行数组，早晚会错位（加了一条中文忘了加英文），而错位是静默的：
 * 提示词里少一个词，没人看得出来。成对存，改一处就够。
 *
 * ## 为什么二级是自己写的，不是照抄阿里的树
 *
 * 阿里的二级（甚至三层）是**给买家检索用的**：一个"五金工具"下会同时出现
 * 材质、工艺、用途三个维度的切分，因为它要覆盖搜索词的分布。工厂不这样说话，
 * 他说的是"我这条线做手工具，那条线做电动工具"。照抄会让二级变成一堆
 * 互相重叠的检索词，而 seeds 也跟着变成关键词而不是商品。
 *
 * ## 为什么是常量文件，不是数据库表
 *
 * 控制台和 platform 都依赖 `@icetus/contracts`，所以一份常量就是唯一真源 ——
 * 不会重演 `BlockField` 有两份、改一处忘了另一处的老问题。加一个二级品类
 * 是加六行数据、跑一次 CI、部署一次，不需要迁移。
 *
 * 真要做到运营自己在后台加的时候，把这份数据灌进一张 `industry_taxonomy` 表、
 * 控制台改读表即可：**结构本来就是照表设计的**，接口和前端都不用动。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.TAXONOMY = exports.TAXONOMY_VERSION = void 0;
exports.industryOptions = industryOptions;
exports.findIndustry = findIndustry;
exports.findSegment = findSegment;
exports.isValidIndustry = isValidIndustry;
exports.isValidSegment = isValidSegment;
exports.pickTheme = pickTheme;
exports.matchSegmentByText = matchSegmentByText;
exports.segmentSeedsEn = segmentSeedsEn;
exports.segmentHintsZh = segmentHintsZh;
const brand_palette_1 = require("./tokens/brand-palette");
/**
 * 版本号，落进 `site_agent_runs.brief.taxonomyVersion`。
 *
 * 半年后你改了二级分类，老 run 仍然能解释"当时为什么出的是这些东西"。
 * 没有它，查历史记录时只会得出"系统当时抽风了"。
 */
exports.TAXONOMY_VERSION = '2026-11-05.1';
exports.TAXONOMY = [
    {
        id: 'apparel-accessories',
        label: '服装与配饰',
        en: 'Apparel & Accessories',
        theme: 'meridian',
        palette: brand_palette_1.BRAND_PALETTES.stone.hex,
        tone: 'editorial',
        segments: [
            {
                id: 'tops-apparel',
                label: '上装',
                en: 'Tops & Shirts',
                aliases: ['上衣', '衬衫', 'T恤', '针织衫'],
                seeds: [
                    { zh: '圆领T恤', en: 'Crew Neck T-Shirt' },
                    { zh: 'polo衫', en: 'Polo Shirt' },
                    { zh: '连帽卫衣', en: 'Hooded Sweatshirt' },
                    { zh: '牛津衬衫', en: 'Oxford Shirt' },
                    { zh: '针织开衫', en: 'Knit Cardigan' },
                    { zh: '背心', en: 'Tank Top' },
                    { zh: '长袖打底衫', en: 'Long Sleeve Base Layer' },
                    { zh: '卫衣', en: 'Pullover Sweatshirt' },
                ],
            },
            {
                id: 'bottoms-apparel',
                label: '下装',
                en: 'Bottoms & Trousers',
                aliases: ['裤子', '长裤', '短裤', '裙装'],
                seeds: [
                    { zh: '牛仔裤', en: 'Denim Jeans' },
                    { zh: '休闲长裤', en: 'Casual Trousers' },
                    { zh: '运动短裤', en: 'Athletic Shorts' },
                    { zh: '工装裤', en: 'Cargo Pants' },
                    { zh: '半身裙', en: 'Skirt' },
                    { zh: '打底裤', en: 'Leggings' },
                    { zh: '西裤', en: 'Dress Pants' },
                    { zh: '阔腿裤', en: 'Wide Leg Pants' },
                ],
            },
            {
                id: 'outerwear',
                label: '外套',
                en: 'Outerwear & Jackets',
                aliases: ['夹克', '大衣', '风衣', '棉服'],
                seeds: [
                    { zh: '羽绒服', en: 'Down Jacket' },
                    { zh: '冲锋衣', en: 'Shell Jacket' },
                    { zh: '牛仔夹克', en: 'Denim Jacket' },
                    { zh: '风衣', en: 'Trench Coat' },
                    { zh: '棉服', en: 'Padded Jacket' },
                    { zh: '西装外套', en: 'Blazer' },
                    { zh: '棒球服', en: 'Bomber Jacket' },
                    { zh: '马甲', en: 'Vest' },
                ],
            },
            {
                id: 'underwear-sleep',
                label: '内衣家居',
                en: 'Underwear & Loungewear',
                aliases: ['内衣', '文胸', '家居服', '睡衣'],
                seeds: [
                    { zh: '文胸', en: 'Bra' },
                    { zh: '内裤', en: 'Underwear' },
                    { zh: '家居服套装', en: 'Loungewear Set' },
                    { zh: '睡衣', en: 'Pajamas' },
                    { zh: '保暖内衣', en: 'Thermal Underwear' },
                    { zh: '袜子', en: 'Socks' },
                    { zh: '塑身衣', en: 'Shapewear' },
                    { zh: '泳衣', en: 'Swimsuit' },
                ],
            },
            {
                id: 'accessories',
                label: '配饰',
                en: 'Fashion Accessories',
                aliases: ['饰品', '帽子', '围巾', '腰带'],
                seeds: [
                    { zh: '棒球帽', en: 'Baseball Cap' },
                    { zh: '针织围巾', en: 'Knit Scarf' },
                    { zh: '皮带', en: 'Leather Belt' },
                    { zh: '手套', en: 'Gloves' },
                    { zh: '太阳镜', en: 'Sunglasses' },
                    { zh: '手提包', en: 'Handbag' },
                    { zh: '领带', en: 'Neck Tie' },
                    { zh: '发饰', en: 'Hair Accessory' },
                ],
            },
            {
                id: 'activewear',
                label: '运动服',
                en: 'Activewear & Sportswear',
                aliases: ['运动装', '健身服', '瑜伽服', '户外服'],
                seeds: [
                    { zh: '瑜伽裤', en: 'Yoga Pants' },
                    { zh: '运动上衣', en: 'Sports Top' },
                    { zh: '跑步短裤', en: 'Running Shorts' },
                    { zh: '运动套装', en: 'Tracksuit' },
                    { zh: '速干T恤', en: 'Quick Dry T-Shirt' },
                    { zh: '健身背心', en: 'Gym Tank' },
                    { zh: '运动外套', en: 'Track Jacket' },
                    { zh: '压缩衣', en: 'Compression Wear' },
                ],
            },
        ],
    },
    {
        id: 'consumer-electronics',
        label: '消费电子',
        en: 'Consumer Electronics',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.steel.hex,
        tone: 'tech',
        segments: [
            {
                id: 'audio-devices',
                label: '音频产品',
                en: 'Audio Devices',
                aliases: ['耳机音箱', '声学产品', '影音设备', '听音设备'],
                seeds: [
                    { zh: '蓝牙耳机', en: 'Bluetooth Earbuds' },
                    { zh: '头戴耳机', en: 'Over-Ear Headphones' },
                    { zh: '蓝牙音箱', en: 'Bluetooth Speaker' },
                    { zh: '回音壁', en: 'Soundbar' },
                    { zh: 'TWS耳机', en: 'True Wireless Earbuds' },
                    { zh: '便携音箱', en: 'Portable Speaker' },
                    { zh: '有线耳麦', en: 'Wired Headset' },
                    { zh: '桌面音箱', en: 'Desktop Speaker' },
                ],
            },
            {
                id: 'charging-power',
                label: '充电电源',
                en: 'Charging & Power',
                aliases: ['充电配件', '移动电源', '电源适配器', '充电器'],
                seeds: [
                    { zh: '移动电源', en: 'Power Bank' },
                    { zh: '充电头', en: 'Wall Charger' },
                    { zh: '车载充电器', en: 'Car Charger' },
                    { zh: '无线充电板', en: 'Wireless Charging Pad' },
                    { zh: '充电数据线', en: 'Charging Cable' },
                    { zh: '多口充电器', en: 'Multi-Port Charger' },
                    { zh: '磁吸充电宝', en: 'Magnetic Power Bank' },
                    { zh: '氮化镓充电器', en: 'GaN Charger' },
                ],
            },
            {
                id: 'smart-wearables',
                label: '智能穿戴',
                en: 'Smart Wearables',
                aliases: ['穿戴设备', '智能手表', '手环', '可穿戴产品'],
                seeds: [
                    { zh: '智能手表', en: 'Smart Watch' },
                    { zh: '运动手环', en: 'Fitness Tracker' },
                    { zh: '智能戒指', en: 'Smart Ring' },
                    { zh: '儿童手表', en: 'Kids Smart Watch' },
                    { zh: '智能眼镜', en: 'Smart Glasses' },
                    { zh: '手表表带', en: 'Watch Band' },
                    { zh: '骨传导耳机', en: 'Bone Conduction Headset' },
                    { zh: '智能手环表壳', en: 'Tracker Case' },
                ],
            },
            {
                id: 'phone-accessories',
                label: '手机配件',
                en: 'Phone Accessories',
                aliases: ['手机周边', '数码配件', '手机壳膜', '配件'],
                seeds: [
                    { zh: '手机壳', en: 'Phone Case' },
                    { zh: '钢化膜', en: 'Tempered Glass Screen Protector' },
                    { zh: '手机支架', en: 'Phone Stand' },
                    { zh: '自拍杆', en: 'Selfie Stick' },
                    { zh: '车载支架', en: 'Car Phone Mount' },
                    { zh: '指环扣', en: 'Phone Ring Holder' },
                    { zh: '手机挂绳', en: 'Phone Lanyard' },
                    { zh: '平板保护套', en: 'Tablet Case' },
                ],
            },
            {
                id: 'computer-peripherals',
                label: '电脑外设',
                en: 'Computer Peripherals',
                aliases: ['电脑配件', '键鼠外设', '办公外设', '数码外设'],
                seeds: [
                    { zh: '无线鼠标', en: 'Wireless Mouse' },
                    { zh: '机械键盘', en: 'Mechanical Keyboard' },
                    { zh: 'USB集线器', en: 'USB Hub' },
                    { zh: '网络摄像头', en: 'Webcam' },
                    { zh: '扩展坞', en: 'Docking Station' },
                    { zh: '鼠标垫', en: 'Mouse Pad' },
                    { zh: '笔记本支架', en: 'Laptop Stand' },
                    { zh: '蓝牙适配器', en: 'Bluetooth Adapter' },
                ],
            },
            {
                id: 'smart-home',
                label: '智能家居',
                en: 'Smart Home Devices',
                aliases: ['智能家居设备', '家居电子', '智能硬件', '物联网设备'],
                seeds: [
                    { zh: '智能插座', en: 'Smart Plug' },
                    { zh: '智能灯泡', en: 'Smart Bulb' },
                    { zh: '智能摄像头', en: 'Smart Camera' },
                    { zh: '智能门锁', en: 'Smart Door Lock' },
                    { zh: '温湿度传感器', en: 'Temperature Sensor' },
                    { zh: '智能开关', en: 'Smart Switch' },
                    { zh: '智能门铃', en: 'Smart Doorbell' },
                    { zh: '智能网关', en: 'Smart Gateway' },
                ],
            },
        ],
    },
    {
        id: 'home-garden',
        label: '家居与园艺',
        en: 'Home & Garden',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.forest.hex,
        tone: 'warm',
        segments: [
            {
                id: 'garden-tools',
                label: '园艺工具',
                en: 'Garden Tools',
                aliases: ['园林工具', '手工具', '园艺器具', '种植工具'],
                seeds: [
                    { zh: '修枝剪', en: 'Pruning Shears' },
                    { zh: '园艺铲', en: 'Garden Trowel' },
                    { zh: '锄头', en: 'Garden Hoe' },
                    { zh: '耙子', en: 'Leaf Rake' },
                    { zh: '浇水壶', en: 'Watering Can' },
                    { zh: '高枝锯', en: 'Pole Pruning Saw' },
                    { zh: '草剪', en: 'Grass Shears' },
                    { zh: '园艺手套', en: 'Garden Gloves' },
                ],
            },
            {
                id: 'outdoor-furniture',
                label: '户外家具',
                en: 'Outdoor Furniture',
                aliases: ['庭院家具', '花园家具', '休闲家具', '户外桌椅'],
                seeds: [
                    { zh: '藤编沙发', en: 'Rattan Sofa' },
                    { zh: '折叠躺椅', en: 'Folding Lounge Chair' },
                    { zh: '户外餐桌', en: 'Patio Dining Table' },
                    { zh: '秋千椅', en: 'Porch Swing' },
                    { zh: '遮阳伞', en: 'Patio Umbrella' },
                    { zh: '吊床', en: 'Hammock' },
                    { zh: '户外储物箱', en: 'Deck Storage Box' },
                    { zh: '摇椅', en: 'Rocking Chair' },
                ],
            },
            {
                id: 'planters',
                label: '花盆花器',
                en: 'Planters & Pots',
                aliases: ['花盆', '种植盆', '花器', '园艺容器'],
                seeds: [
                    { zh: '陶瓷花盆', en: 'Ceramic Planter' },
                    { zh: '塑料花盆', en: 'Plastic Flower Pot' },
                    { zh: '水泥花盆', en: 'Concrete Planter' },
                    { zh: '悬挂花篮', en: 'Hanging Basket' },
                    { zh: '种植箱', en: 'Raised Planter Box' },
                    { zh: '自吸水花盆', en: 'Self-Watering Pot' },
                    { zh: '铁艺花架', en: 'Metal Plant Stand' },
                    { zh: '育苗盘', en: 'Seedling Tray' },
                ],
            },
            {
                id: 'home-storage',
                label: '家居收纳',
                en: 'Home Storage',
                aliases: ['收纳用品', '储物用品', '整理箱', '家居整理'],
                seeds: [
                    { zh: '收纳箱', en: 'Storage Bin' },
                    { zh: '布艺收纳袋', en: 'Fabric Storage Bag' },
                    { zh: '衣柜挂袋', en: 'Hanging Closet Organizer' },
                    { zh: '鞋架', en: 'Shoe Rack' },
                    { zh: '脏衣篮', en: 'Laundry Hamper' },
                    { zh: '桌面收纳盒', en: 'Desk Organizer' },
                    { zh: '真空压缩袋', en: 'Vacuum Storage Bag' },
                    { zh: '置物架', en: 'Shelving Unit' },
                ],
            },
            {
                id: 'home-textiles',
                label: '家纺布艺',
                en: 'Home Textiles',
                aliases: ['家用纺织品', '布艺家居', '床上用品', '家居布艺'],
                seeds: [
                    { zh: '抱枕套', en: 'Throw Pillow Cover' },
                    { zh: '沙发毯', en: 'Sofa Throw Blanket' },
                    { zh: '桌布', en: 'Tablecloth' },
                    { zh: '窗帘', en: 'Window Curtain' },
                    { zh: '地垫', en: 'Floor Mat' },
                    { zh: '床品四件套', en: 'Bedding Set' },
                    { zh: '厨房围裙', en: 'Kitchen Apron' },
                    { zh: '餐垫', en: 'Placemat' },
                ],
            },
            {
                id: 'home-decor',
                label: '家居装饰',
                en: 'Home Decor',
                aliases: ['家居饰品', '装饰摆件', '软装饰品', '家居摆件'],
                seeds: [
                    { zh: '香薰蜡烛', en: 'Scented Candle' },
                    { zh: '仿真花', en: 'Artificial Flower' },
                    { zh: '装饰画', en: 'Wall Art Print' },
                    { zh: '相框', en: 'Picture Frame' },
                    { zh: '花瓶', en: 'Flower Vase' },
                    { zh: '墙面挂钩', en: 'Wall Hook' },
                    { zh: '摆件', en: 'Decorative Figurine' },
                    { zh: '串灯', en: 'String Lights' },
                ],
            },
        ],
    },
    {
        id: 'sports-entertainment',
        label: '运动与娱乐',
        en: 'Sports & Entertainment',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.clay.hex,
        tone: 'editorial',
        segments: [
            {
                id: 'fitness-equipment',
                label: '健身器材',
                en: 'Fitness Equipment',
                aliases: ['健身器械', '力量器材', '家用健身', '训练器材'],
                seeds: [
                    { zh: '哑铃', en: 'Dumbbell Set' },
                    { zh: '杠铃片', en: 'Olympic Weight Plate' },
                    { zh: '壶铃', en: 'Kettlebell' },
                    { zh: '跑步机', en: 'Treadmill' },
                    { zh: '动感单车', en: 'Spin Bike' },
                    { zh: '瑜伽垫', en: 'Yoga Mat' },
                    { zh: '引体向上架', en: 'Pull Up Bar' },
                    { zh: '弹力带', en: 'Resistance Band' },
                ],
            },
            {
                id: 'ball-sports',
                label: '球类用品',
                en: 'Ball Sports',
                aliases: ['球类', '运动球', '比赛用球', '训练球'],
                seeds: [
                    { zh: '足球', en: 'Soccer Ball' },
                    { zh: '篮球', en: 'Basketball' },
                    { zh: '排球', en: 'Volleyball' },
                    { zh: '羽毛球', en: 'Badminton Shuttlecock' },
                    { zh: '网球', en: 'Tennis Ball' },
                    { zh: '乒乓球', en: 'Table Tennis Ball' },
                    { zh: '手球', en: 'Handball' },
                    { zh: '橄榄球', en: 'Rugby Ball' },
                ],
            },
            {
                id: 'water-sports',
                label: '水上运动',
                en: 'Water Sports',
                aliases: ['游泳用品', '水上用品', '潜水装备', '泳池用品'],
                seeds: [
                    { zh: '游泳镜', en: 'Swimming Goggles' },
                    { zh: '脚蹼', en: 'Swim Fins' },
                    { zh: '浮板', en: 'Kickboard' },
                    { zh: '潜水镜', en: 'Dive Mask' },
                    { zh: '呼吸管', en: 'Snorkel Tube' },
                    { zh: '救生衣', en: 'Life Vest' },
                    { zh: '冲浪板', en: 'Surfboard' },
                    { zh: '泳帽', en: 'Swim Cap' },
                ],
            },
            {
                id: 'camping-outdoor',
                label: '户外露营',
                en: 'Camping Outdoor',
                aliases: ['露营装备', '户外用品', '野营装备', '帐篷用品'],
                seeds: [
                    { zh: '帐篷', en: 'Camping Tent' },
                    { zh: '睡袋', en: 'Sleeping Bag' },
                    { zh: '折叠椅', en: 'Folding Chair' },
                    { zh: '防潮垫', en: 'Camping Mat' },
                    { zh: '登山杖', en: 'Trekking Pole' },
                    { zh: '头灯', en: 'Headlamp' },
                    { zh: '保温水壶', en: 'Insulated Bottle' },
                    { zh: '烧烤炉', en: 'BBQ Grill' },
                ],
            },
            {
                id: 'playground-rides',
                label: '游乐设备',
                en: 'Playground Rides',
                aliases: ['游乐设施', '儿童游乐', '玩具设备', '娱乐设备'],
                seeds: [
                    { zh: '秋千', en: 'Swing Set' },
                    { zh: '滑梯', en: 'Playground Slide' },
                    { zh: '蹦床', en: 'Trampoline' },
                    { zh: '跷跷板', en: 'Seesaw' },
                    { zh: '摇摇马', en: 'Rocking Horse' },
                    { zh: '攀爬架', en: 'Climbing Frame' },
                    { zh: '充气城堡', en: 'Inflatable Castle' },
                    { zh: '沙池', en: 'Sand Pit' },
                ],
            },
            {
                id: 'racket-court',
                label: '拍类运动',
                en: 'Racket Sports',
                aliases: ['球拍', '网羽用品', '拍类用品', '球网'],
                seeds: [
                    { zh: '羽毛球拍', en: 'Badminton Racket' },
                    { zh: '网球拍', en: 'Tennis Racket' },
                    { zh: '乒乓球拍', en: 'Table Tennis Paddle' },
                    { zh: '球网', en: 'Sports Net' },
                    { zh: '球拍线', en: 'Racket String' },
                    { zh: '壁球拍', en: 'Squash Racket' },
                    { zh: '拍套', en: 'Racket Cover' },
                    { zh: '乒乓球台', en: 'Table Tennis Table' },
                ],
            },
        ],
    },
    {
        id: 'jewelry-eyewear-watches',
        label: '珠宝眼镜与钟表',
        en: 'Jewelry, Eyewear & Watches',
        theme: 'meridian',
        palette: brand_palette_1.BRAND_PALETTES.charcoal.hex,
        tone: 'editorial',
        segments: [
            {
                id: 'fashion-jewelry',
                label: '流行饰品',
                en: 'Fashion Jewelry',
                aliases: ['仿真首饰', '合金饰品', '时尚首饰', '配饰'],
                seeds: [
                    { zh: '合金项链', en: 'Alloy Necklace' },
                    { zh: '水钻耳环', en: 'Rhinestone Earrings' },
                    { zh: '不锈钢手链', en: 'Stainless Steel Bracelet' },
                    { zh: '珍珠胸针', en: 'Pearl Brooch' },
                    { zh: '锆石戒指', en: 'Cubic Zirconia Ring' },
                    { zh: '珐琅吊坠', en: 'Enamel Pendant' },
                    { zh: '脚链', en: 'Anklet' },
                    { zh: '发夹饰品', en: 'Hair Clip Accessory' },
                ],
            },
            {
                id: 'fine-jewelry',
                label: '金银珠宝',
                en: 'Fine Jewelry',
                aliases: ['贵金属首饰', 'K金首饰', '珠宝首饰', '真金白银'],
                seeds: [
                    { zh: '黄金戒指', en: 'Gold Ring' },
                    { zh: '银项链', en: 'Silver Necklace' },
                    { zh: '钻石耳钉', en: 'Diamond Stud Earrings' },
                    { zh: '翡翠吊坠', en: 'Jadeite Pendant' },
                    { zh: '红宝石手链', en: 'Ruby Bracelet' },
                    { zh: '铂金对戒', en: 'Platinum Couple Rings' },
                    { zh: '和田玉手镯', en: 'Hetian Jade Bangle' },
                    { zh: '蓝宝石胸针', en: 'Sapphire Brooch' },
                ],
            },
            {
                id: 'eyewear',
                label: '眼镜',
                en: 'Eyewear',
                aliases: ['太阳镜', '光学镜架', '眼镜框', '墨镜'],
                seeds: [
                    { zh: '金属光学镜架', en: 'Metal Optical Frame' },
                    { zh: '板材太阳镜', en: 'Acetate Sunglasses' },
                    { zh: 'TR90镜架', en: 'TR90 Eyeglass Frame' },
                    { zh: '钛合金镜架', en: 'Titanium Eyeglass Frame' },
                    { zh: '偏光太阳镜', en: 'Polarized Sunglasses' },
                    { zh: '老花镜', en: 'Reading Glasses' },
                    { zh: '儿童眼镜', en: 'Kids Eyeglasses' },
                    { zh: '运动护目镜', en: 'Sports Goggles' },
                ],
            },
            {
                id: 'watch-cases',
                label: '表壳表带',
                en: 'Watch Cases & Bands',
                aliases: ['手表配件', '表壳', '表带', '钟表配件'],
                seeds: [
                    { zh: '不锈钢表壳', en: 'Stainless Steel Watch Case' },
                    { zh: '真皮表带', en: 'Leather Watch Strap' },
                    { zh: '钢带', en: 'Steel Watch Band' },
                    { zh: '硅胶表带', en: 'Silicone Watch Strap' },
                    { zh: '表盘', en: 'Watch Dial' },
                    { zh: '表扣', en: 'Watch Clasp' },
                    { zh: '表冠', en: 'Watch Crown' },
                    { zh: '尼龙表带', en: 'Nylon Watch Strap' },
                ],
            },
            {
                id: 'finished-watches',
                label: '成品钟表',
                en: 'Finished Watches & Clocks',
                aliases: ['手表', '钟表', '腕表', '时钟'],
                seeds: [
                    { zh: '石英手表', en: 'Quartz Watch' },
                    { zh: '机械手表', en: 'Mechanical Watch' },
                    { zh: '智能手表', en: 'Smart Watch' },
                    { zh: '挂钟', en: 'Wall Clock' },
                    { zh: '台钟', en: 'Desk Clock' },
                    { zh: '闹钟', en: 'Alarm Clock' },
                    { zh: '怀表', en: 'Pocket Watch' },
                    { zh: '儿童手表', en: 'Kids Watch' },
                ],
            },
            {
                id: 'jewelry-parts',
                label: '首饰配件',
                en: 'Jewelry Components',
                aliases: ['饰品配件', '首饰半成品', '珠宝配件', 'DIY配件'],
                seeds: [
                    { zh: '合金链条', en: 'Alloy Chain' },
                    { zh: '耳钩', en: 'Ear Wire' },
                    { zh: '龙虾扣', en: 'Lobster Clasp' },
                    { zh: '吊坠托', en: 'Pendant Setting' },
                    { zh: '戒托', en: 'Ring Setting' },
                    { zh: '串珠', en: 'Loose Beads' },
                    { zh: '首饰展示架', en: 'Jewelry Display Stand' },
                    { zh: '首饰包装盒', en: 'Jewelry Gift Box' },
                ],
            },
        ],
    },
    {
        id: 'sportswear-outdoor',
        label: '运动户外服装',
        en: 'Sportswear & Outdoor Apparel',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.clay.hex,
        tone: 'editorial',
        segments: [
            {
                id: 'outdoor-jackets',
                label: '户外外套',
                en: 'Outdoor Jackets',
                aliases: ['冲锋衣', '防风衣', '登山服', '滑雪服', '软壳衣'],
                seeds: [
                    { zh: '冲锋衣', en: 'Waterproof Shell Jacket' },
                    { zh: '软壳衣', en: 'Softshell Jacket' },
                    { zh: '抓绒衣', en: 'Fleece Jacket' },
                    { zh: '羽绒服', en: 'Down Jacket' },
                    { zh: '滑雪服', en: 'Ski Jacket' },
                    { zh: '防风马甲', en: 'Windproof Vest' },
                    { zh: '雨衣', en: 'Rain Jacket' },
                    { zh: '棉服', en: 'Padded Jacket' },
                ],
            },
            {
                id: 'sport-tops',
                label: '运动上衣',
                en: 'Sport Tops',
                aliases: ['速干衣', '运动T恤', '健身服', '跑步服', '训练服'],
                seeds: [
                    { zh: '速干T恤', en: 'Quick-Dry T-Shirt' },
                    { zh: '运动背心', en: 'Sports Tank Top' },
                    { zh: '连帽卫衣', en: 'Hooded Sweatshirt' },
                    { zh: '长袖训练衫', en: 'Long Sleeve Training Top' },
                    { zh: 'Polo衫', en: 'Sports Polo Shirt' },
                    { zh: '瑜伽上衣', en: 'Yoga Top' },
                    { zh: '压缩衣', en: 'Compression Shirt' },
                    { zh: '运动内衣', en: 'Sports Bra' },
                ],
            },
            {
                id: 'sport-bottoms',
                label: '运动裤',
                en: 'Sport Bottoms',
                aliases: ['速干裤', '健身裤', '跑步裤', '户外裤'],
                seeds: [
                    { zh: '运动长裤', en: 'Track Pants' },
                    { zh: '紧身裤', en: 'Leggings' },
                    { zh: '运动短裤', en: 'Sports Shorts' },
                    { zh: '速干裤', en: 'Quick-Dry Pants' },
                    { zh: '软壳裤', en: 'Softshell Pants' },
                    { zh: '冲锋裤', en: 'Waterproof Pants' },
                    { zh: '瑜伽裤', en: 'Yoga Pants' },
                    { zh: '工装短裤', en: 'Cargo Shorts' },
                ],
            },
            {
                id: 'base-layers',
                label: '贴身层',
                en: 'Base Layers',
                aliases: ['打底衫', '保暖内衣', '速干内衣', '排汗衫', '内搭'],
                seeds: [
                    { zh: '速干内衣', en: 'Moisture-Wicking Base Layer' },
                    { zh: '保暖内衣', en: 'Thermal Underwear' },
                    { zh: '压缩裤', en: 'Compression Tights' },
                    { zh: '排汗T恤', en: 'Sweat-Wicking Shirt' },
                    { zh: '运动袜', en: 'Sports Socks' },
                    { zh: '无缝内衣', en: 'Seamless Underwear' },
                    { zh: '打底长裤', en: 'Base Layer Pants' },
                    { zh: '运动内裤', en: 'Sports Briefs' },
                ],
            },
            {
                id: 'outdoor-gear',
                label: '户外配件',
                en: 'Outdoor Accessories',
                aliases: ['运动配件', '帽子手套', '户外小件', '穿戴配件'],
                seeds: [
                    { zh: '棒球帽', en: 'Baseball Cap' },
                    { zh: '针织帽', en: 'Knit Beanie' },
                    { zh: '运动手套', en: 'Sports Gloves' },
                    { zh: '魔术头巾', en: 'Neck Gaiter' },
                    { zh: '运动腰包', en: 'Running Belt' },
                    { zh: '防晒袖套', en: 'Sun Sleeves' },
                    { zh: '护膝', en: 'Knee Support' },
                    { zh: '运动背包', en: 'Sports Backpack' },
                ],
            },
            {
                id: 'swim-active',
                label: '泳装健身',
                en: 'Swim & Activewear',
                aliases: ['泳装', '健身服', '瑜伽服', '水上运动服', '舞蹈服'],
                seeds: [
                    { zh: '连体泳衣', en: 'One-Piece Swimsuit' },
                    { zh: '分体泳衣', en: 'Two-Piece Swimsuit' },
                    { zh: '泳裤', en: 'Swim Trunks' },
                    { zh: '瑜伽套装', en: 'Yoga Set' },
                    { zh: '健身背心', en: 'Gym Tank Top' },
                    { zh: '舞蹈紧身衣', en: 'Dance Leotard' },
                    { zh: '防晒冲浪服', en: 'Rash Guard' },
                    { zh: '沙滩裤', en: 'Beach Shorts' },
                ],
            },
        ],
    },
    {
        id: 'beauty',
        label: '美妆',
        en: 'Beauty',
        theme: 'meridian',
        palette: brand_palette_1.BRAND_PALETTES.aubergine.hex,
        tone: 'editorial',
        segments: [
            {
                id: 'color-cosmetics',
                label: '彩妆',
                en: 'Color Cosmetics',
                aliases: ['彩妆用品', '化妆彩妆', '彩妆产品', '美妆彩妆'],
                seeds: [
                    { zh: '口红', en: 'Lipstick' },
                    { zh: '粉底液', en: 'Liquid Foundation' },
                    { zh: '眼影盘', en: 'Eyeshadow Palette' },
                    { zh: '睫毛膏', en: 'Mascara' },
                    { zh: '腮红', en: 'Blush Powder' },
                    { zh: '唇釉', en: 'Lip Gloss' },
                    { zh: '遮瑕膏', en: 'Concealer Cream' },
                    { zh: '眼线笔', en: 'Eyeliner Pen' },
                ],
            },
            {
                id: 'skincare',
                label: '护肤品',
                en: 'Skincare Products',
                aliases: ['护肤用品', '面部护理', '保养品', '护肤产品'],
                seeds: [
                    { zh: '面霜', en: 'Face Cream' },
                    { zh: '精华液', en: 'Facial Serum' },
                    { zh: '洁面乳', en: 'Facial Cleanser' },
                    { zh: '爽肤水', en: 'Toner Water' },
                    { zh: '面膜', en: 'Facial Mask' },
                    { zh: '防晒霜', en: 'Sunscreen Lotion' },
                    { zh: '眼霜', en: 'Eye Cream' },
                    { zh: '卸妆油', en: 'Cleansing Oil' },
                ],
            },
            {
                id: 'makeup-tools',
                label: '美妆工具',
                en: 'Makeup Tools',
                aliases: ['化妆工具', '美妆刷具', '化妆用具', '美妆配件'],
                seeds: [
                    { zh: '化妆刷', en: 'Makeup Brush' },
                    { zh: '美妆蛋', en: 'Beauty Sponge' },
                    { zh: '粉扑', en: 'Powder Puff' },
                    { zh: '睫毛夹', en: 'Eyelash Curler' },
                    { zh: '化妆棉', en: 'Cotton Pad' },
                    { zh: '修眉刀', en: 'Eyebrow Razor' },
                    { zh: '化妆包', en: 'Cosmetic Bag' },
                    { zh: '假睫毛', en: 'False Eyelashes' },
                ],
            },
            {
                id: 'nail-care',
                label: '美甲',
                en: 'Nail Products',
                aliases: ['美甲用品', '指甲护理', '美甲产品', '甲油'],
                seeds: [
                    { zh: '指甲油', en: 'Nail Polish' },
                    { zh: '甲油胶', en: 'Gel Nail Polish' },
                    { zh: '卸甲水', en: 'Nail Polish Remover' },
                    { zh: '指甲锉', en: 'Nail File' },
                    { zh: '死皮剪', en: 'Cuticle Nipper' },
                    { zh: '美甲灯', en: 'Nail UV Lamp' },
                    { zh: '指甲贴片', en: 'Press-On Nails' },
                    { zh: '护甲油', en: 'Nail Strengthener' },
                ],
            },
            {
                id: 'hair-care',
                label: '洗护发',
                en: 'Hair Care',
                aliases: ['头发护理', '洗护用品', '美发产品', '护发产品'],
                seeds: [
                    { zh: '洗发水', en: 'Shampoo' },
                    { zh: '护发素', en: 'Hair Conditioner' },
                    { zh: '发膜', en: 'Hair Mask' },
                    { zh: '护发精油', en: 'Hair Oil Serum' },
                    { zh: '定型喷雾', en: 'Hair Spray' },
                    { zh: '染发膏', en: 'Hair Color Cream' },
                    { zh: '卷发棒', en: 'Curling Iron' },
                    { zh: '吹风机', en: 'Hair Dryer' },
                ],
            },
            {
                id: 'fragrance-body',
                label: '香氛身体',
                en: 'Fragrance and Body Care',
                aliases: ['香水香氛', '身体护理', '香氛用品', '身体洗护'],
                seeds: [
                    { zh: '香水', en: 'Perfume Spray' },
                    { zh: '香薰蜡烛', en: 'Scented Candle' },
                    { zh: '身体乳', en: 'Body Lotion' },
                    { zh: '沐浴露', en: 'Body Wash' },
                    { zh: '护手霜', en: 'Hand Cream' },
                    { zh: '香体喷雾', en: 'Body Mist' },
                    { zh: '磨砂膏', en: 'Body Scrub' },
                    { zh: '精油', en: 'Essential Oil' },
                ],
            },
        ],
    },
    {
        id: 'shoes-accessories',
        label: '鞋靴与配饰',
        en: 'Shoes & Accessories',
        theme: 'meridian',
        palette: brand_palette_1.BRAND_PALETTES.stone.hex,
        tone: 'editorial',
        segments: [
            {
                id: 'casual-shoes',
                label: '休闲鞋',
                en: 'Casual Shoes',
                aliases: ['日常鞋', '板鞋', '帆布鞋', '便鞋'],
                seeds: [
                    { zh: '帆布鞋', en: 'Canvas Sneakers' },
                    { zh: '小白鞋', en: 'White Leather Sneakers' },
                    { zh: '乐福鞋', en: 'Leather Loafers' },
                    { zh: '一脚蹬', en: 'Slip-On Shoes' },
                    { zh: '老爹鞋', en: 'Chunky Sneakers' },
                    { zh: '帆布便鞋', en: 'Canvas Slip-Ons' },
                    { zh: '德比鞋', en: 'Derby Shoes' },
                    { zh: '豆豆鞋', en: 'Driving Moccasins' },
                ],
            },
            {
                id: 'boots',
                label: '靴子',
                en: 'Boots',
                aliases: ['长靴', '短靴', '马丁靴', '工装靴'],
                seeds: [
                    { zh: '马丁靴', en: 'Combat Boots' },
                    { zh: '切尔西靴', en: 'Chelsea Boots' },
                    { zh: '雪地靴', en: 'Snow Boots' },
                    { zh: '工装靴', en: 'Work Boots' },
                    { zh: '登山靴', en: 'Hiking Boots' },
                    { zh: '雨靴', en: 'Rain Boots' },
                    { zh: '牛仔靴', en: 'Cowboy Boots' },
                    { zh: '沙漠靴', en: 'Desert Boots' },
                ],
            },
            {
                id: 'sandals-slippers',
                label: '凉鞋拖鞋',
                en: 'Sandals & Slippers',
                aliases: ['凉拖', '人字拖', '沙滩鞋', '居家拖鞋'],
                seeds: [
                    { zh: '人字拖', en: 'Flip Flops' },
                    { zh: '沙滩凉鞋', en: 'Beach Sandals' },
                    { zh: '一字拖', en: 'Slide Sandals' },
                    { zh: '居家拖鞋', en: 'House Slippers' },
                    { zh: '毛绒拖鞋', en: 'Fuzzy Slippers' },
                    { zh: '运动凉鞋', en: 'Sport Sandals' },
                    { zh: '坡跟凉鞋', en: 'Wedge Sandals' },
                    { zh: '洞洞鞋', en: 'Clogs' },
                ],
            },
            {
                id: 'shoe-uppers',
                label: '鞋面鞋底',
                en: 'Shoe Uppers & Soles',
                aliases: ['鞋材', '鞋底', '鞋面', '大底'],
                seeds: [
                    { zh: '橡胶大底', en: 'Rubber Outsoles' },
                    { zh: 'EVA鞋底', en: 'EVA Soles' },
                    { zh: 'PU鞋面', en: 'PU Uppers' },
                    { zh: '飞织鞋面', en: 'Flyknit Uppers' },
                    { zh: '真皮鞋面', en: 'Leather Uppers' },
                    { zh: 'TPR鞋底', en: 'TPR Soles' },
                    { zh: '中底板', en: 'Insole Boards' },
                    { zh: '鞋跟', en: 'Shoe Heels' },
                ],
            },
            {
                id: 'shoe-care',
                label: '鞋护理',
                en: 'Shoe Care',
                aliases: ['鞋油', '清洁剂', '鞋保养', '护理用品'],
                seeds: [
                    { zh: '鞋油', en: 'Shoe Polish' },
                    { zh: '鞋刷', en: 'Shoe Brushes' },
                    { zh: '防水喷雾', en: 'Waterproof Spray' },
                    { zh: '清洁泡沫', en: 'Sneaker Cleaning Foam' },
                    { zh: '鞋撑', en: 'Shoe Trees' },
                    { zh: '除臭剂', en: 'Shoe Deodorizer' },
                    { zh: '麂皮橡皮擦', en: 'Suede Eraser' },
                    { zh: '鞋带', en: 'Shoelaces' },
                ],
            },
            {
                id: 'shoe-accessories',
                label: '鞋配件',
                en: 'Shoe Accessories',
                aliases: ['鞋垫', '鞋扣', '鞋花', '辅料'],
                seeds: [
                    { zh: '鞋垫', en: 'Insoles' },
                    { zh: '鞋花', en: 'Shoe Charms' },
                    { zh: '鞋扣', en: 'Shoe Buckles' },
                    { zh: '后跟贴', en: 'Heel Grips' },
                    { zh: '半码垫', en: 'Half Insoles' },
                    { zh: '鞋拔', en: 'Shoe Horns' },
                    { zh: '防磨贴', en: 'Blister Patches' },
                    { zh: '鞋眼扣', en: 'Metal Eyelets' },
                ],
            },
        ],
    },
    {
        id: 'luggage-bags',
        label: '箱包',
        en: 'Luggage, Bags & Cases',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.slate.hex,
        tone: 'minimal',
        segments: [
            {
                id: 'travel-luggage',
                label: '拉杆箱',
                en: 'Travel Luggage',
                aliases: ['行李箱', '旅行箱', '登机箱', '硬箱', '软箱'],
                seeds: [
                    { zh: '万向轮拉杆箱', en: 'Spinner Wheel Trolley Case' },
                    { zh: '铝框箱', en: 'Aluminum Frame Luggage' },
                    { zh: 'PC硬壳箱', en: 'PC Hard Shell Suitcase' },
                    { zh: '布艺拉杆箱', en: 'Soft Fabric Trolley Case' },
                    { zh: '登机箱', en: 'Cabin Carry-On Case' },
                    { zh: '儿童拉杆箱', en: 'Kids Trolley Luggage' },
                    { zh: '折叠旅行包', en: 'Foldable Travel Bag' },
                    { zh: '行李套箱', en: 'Luggage Set' },
                ],
            },
            {
                id: 'backpacks',
                label: '背包',
                en: 'Backpacks',
                aliases: ['双肩包', '书包', '电脑包', '登山包', '休闲包'],
                seeds: [
                    { zh: '双肩背包', en: 'Casual Daypack' },
                    { zh: '电脑背包', en: 'Laptop Backpack' },
                    { zh: '登山背包', en: 'Hiking Backpack' },
                    { zh: '学生书包', en: 'School Backpack' },
                    { zh: '防盗背包', en: 'Anti-Theft Backpack' },
                    { zh: '束口背包', en: 'Drawstring Backpack' },
                    { zh: '保温背包', en: 'Insulated Cooler Backpack' },
                    { zh: '折叠背包', en: 'Packable Backpack' },
                ],
            },
            {
                id: 'handbags',
                label: '女包',
                en: 'Handbags & Purses',
                aliases: ['手提包', '单肩包', '斜挎包', '钱包', '手袋'],
                seeds: [
                    { zh: '手提包', en: 'Tote Handbag' },
                    { zh: '单肩包', en: 'Shoulder Bag' },
                    { zh: '斜挎包', en: 'Crossbody Bag' },
                    { zh: '链条包', en: 'Chain Shoulder Bag' },
                    { zh: '水桶包', en: 'Bucket Bag' },
                    { zh: '贝壳包', en: 'Dome Satchel Bag' },
                    { zh: '手拿包', en: 'Clutch Purse' },
                    { zh: '长款钱包', en: 'Long Wallet' },
                ],
            },
            {
                id: 'business-bags',
                label: '商务包',
                en: 'Business & Laptop Bags',
                aliases: ['公文包', '电脑包', '男包', '手提包', '文件包'],
                seeds: [
                    { zh: '公文包', en: 'Leather Briefcase' },
                    { zh: '电脑手提包', en: 'Laptop Briefcase' },
                    { zh: '男士手包', en: "Men's Clutch Bag" },
                    { zh: '单肩邮差包', en: 'Messenger Bag' },
                    { zh: '内胆包', en: 'Laptop Sleeve' },
                    { zh: '电脑双肩包', en: 'Business Laptop Backpack' },
                    { zh: '证件包', en: 'Document Organizer Bag' },
                    { zh: '皮带包', en: 'Waist Bag' },
                ],
            },
            {
                id: 'storage-cases',
                label: '收纳包',
                en: 'Storage & Cosmetic Cases',
                aliases: ['化妆包', '洗漱包', '收纳袋', '整理包', '旅行收纳'],
                seeds: [
                    { zh: '化妆包', en: 'Cosmetic Pouch' },
                    { zh: '洗漱包', en: 'Toiletry Bag' },
                    { zh: '收纳袋', en: 'Packing Cube' },
                    { zh: '鞋袋', en: 'Shoe Bag' },
                    { zh: '衣物收纳包', en: 'Travel Organizer Bag' },
                    { zh: '笔袋', en: 'Pencil Case' },
                    { zh: '数码收纳包', en: 'Electronics Organizer Pouch' },
                    { zh: '压缩收纳袋', en: 'Compression Storage Bag' },
                ],
            },
            {
                id: 'specialty-bags',
                label: '功能包',
                en: 'Specialty & Functional Bags',
                aliases: ['运动包', '腰包', '购物袋', '环保袋', '工具包'],
                seeds: [
                    { zh: '运动健身包', en: 'Gym Duffel Bag' },
                    { zh: '腰包', en: 'Fanny Pack' },
                    { zh: '购物袋', en: 'Shopping Tote Bag' },
                    { zh: '无纺布袋', en: 'Non-Woven Bag' },
                    { zh: '工具包', en: 'Tool Bag' },
                    { zh: '保温袋', en: 'Insulated Lunch Bag' },
                    { zh: '骑行包', en: 'Bike Frame Bag' },
                    { zh: '宠物包', en: 'Pet Carrier Bag' },
                ],
            },
        ],
    },
    {
        id: 'packaging-printing',
        label: '包装与印刷',
        en: 'Packaging & Printing',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.steel.hex,
        tone: 'minimal',
        segments: [
            {
                id: 'corrugated-boxes',
                label: '纸箱纸盒',
                en: 'Corrugated Boxes',
                aliases: ['瓦楞纸箱', '包装纸箱', '运输纸箱', '纸盒包装'],
                seeds: [
                    { zh: '瓦楞纸箱', en: 'Corrugated Shipping Box' },
                    { zh: '彩印纸盒', en: 'Printed Folding Carton' },
                    { zh: '天地盖礼盒', en: 'Rigid Gift Box' },
                    { zh: '飞机盒', en: 'Die-Cut Mailer Box' },
                    { zh: '牛皮纸盒', en: 'Kraft Paper Box' },
                    { zh: '蜂窝纸箱', en: 'Honeycomb Paper Box' },
                    { zh: '异形纸盒', en: 'Custom Shape Carton' },
                    { zh: '重型纸箱', en: 'Heavy Duty Carton' },
                ],
            },
            {
                id: 'flexible-packaging',
                label: '软包装',
                en: 'Flexible Packaging',
                aliases: ['塑料包装袋', '复合袋', '包装膜', '软包袋'],
                seeds: [
                    { zh: '自立袋', en: 'Stand-Up Pouch' },
                    { zh: '三边封袋', en: 'Three-Side Seal Bag' },
                    { zh: '铝箔袋', en: 'Aluminum Foil Bag' },
                    { zh: '真空袋', en: 'Vacuum Bag' },
                    { zh: '卷膜', en: 'Roll Film' },
                    { zh: '牛皮纸袋', en: 'Kraft Paper Bag' },
                    { zh: '拉链袋', en: 'Zipper Pouch' },
                    { zh: '吸嘴袋', en: 'Spout Pouch' },
                ],
            },
            {
                id: 'labels-stickers',
                label: '标签贴纸',
                en: 'Labels & Stickers',
                aliases: ['不干胶标签', '商标贴纸', '标签印刷', '贴标'],
                seeds: [
                    { zh: '不干胶标签', en: 'Self-Adhesive Label' },
                    { zh: '铜版纸标签', en: 'Coated Paper Label' },
                    { zh: '透明PVC标签', en: 'Clear PVC Label' },
                    { zh: '热敏标签', en: 'Thermal Label' },
                    { zh: '卷筒标签', en: 'Roll Label' },
                    { zh: '镭射标签', en: 'Holographic Label' },
                    { zh: '易碎贴', en: 'Tamper Evident Sticker' },
                    { zh: '吊牌', en: 'Hang Tag' },
                ],
            },
            {
                id: 'printing-services',
                label: '印刷服务',
                en: 'Printing Services',
                aliases: ['商业印刷', '彩印', '印刷加工', '书刊印刷'],
                seeds: [
                    { zh: '画册印刷', en: 'Catalogue Printing' },
                    { zh: '宣传单页', en: 'Flyer Printing' },
                    { zh: '名片印刷', en: 'Business Card Printing' },
                    { zh: '海报印刷', en: 'Poster Printing' },
                    { zh: '书刊印刷', en: 'Book Printing' },
                    { zh: '台历印刷', en: 'Calendar Printing' },
                    { zh: '纸袋印刷', en: 'Paper Bag Printing' },
                    { zh: '笔记本印刷', en: 'Notebook Printing' },
                ],
            },
            {
                id: 'protective-packaging',
                label: '缓冲包装',
                en: 'Protective Packaging',
                aliases: ['防震包装', '缓冲材料', '内包装', '填充包装'],
                seeds: [
                    { zh: '气泡袋', en: 'Bubble Mailer' },
                    { zh: '珍珠棉', en: 'EPE Foam Sheet' },
                    { zh: '气泡膜', en: 'Bubble Wrap Roll' },
                    { zh: '蜂窝纸板', en: 'Honeycomb Paper Board' },
                    { zh: '纸护角', en: 'Paper Edge Protector' },
                    { zh: '缠绕膜', en: 'Stretch Wrap Film' },
                    { zh: '泡沫箱', en: 'Foam Cooler Box' },
                    { zh: '充气袋', en: 'Air Pillow Bag' },
                ],
            },
            {
                id: 'tape-strapping',
                label: '胶带打包',
                en: 'Tape & Strapping',
                aliases: ['封箱胶带', '打包带', '捆扎带', '封箱材料'],
                seeds: [
                    { zh: '封箱胶带', en: 'Carton Sealing Tape' },
                    { zh: '透明胶带', en: 'Clear Packing Tape' },
                    { zh: '印字胶带', en: 'Printed Packing Tape' },
                    { zh: 'PP打包带', en: 'PP Strapping Band' },
                    { zh: 'PET打包带', en: 'PET Strapping Band' },
                    { zh: '纤维打包带', en: 'Composite Strapping' },
                    { zh: '打包扣', en: 'Strapping Buckle' },
                    { zh: '缠绕胶带', en: 'Stretch Tape' },
                ],
            },
        ],
    },
    {
        id: 'kids-toys',
        label: '母婴与玩具',
        en: 'Parents, Kids & Toys',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.clay.hex,
        tone: 'warm',
        segments: [
            {
                id: 'baby-strollers',
                label: '婴儿推车',
                en: 'Baby Strollers',
                aliases: ['童车', '推车', '婴儿车', '儿童推车'],
                seeds: [
                    { zh: '高景观推车', en: 'High Landscape Stroller' },
                    { zh: '轻便伞车', en: 'Lightweight Umbrella Stroller' },
                    { zh: '双人推车', en: 'Double Stroller' },
                    { zh: '婴儿提篮', en: 'Infant Car Seat Carrier' },
                    { zh: '遛娃神器', en: 'Toddler Trike Stroller' },
                    { zh: '三轮推车', en: 'Three-Wheel Jogging Stroller' },
                    { zh: '推车雨罩', en: 'Stroller Rain Cover' },
                    { zh: '推车坐垫', en: 'Stroller Seat Liner' },
                ],
            },
            {
                id: 'baby-gear',
                label: '婴儿用品',
                en: 'Baby Gear',
                aliases: ['母婴用品', '育儿用品', '宝宝用品', '婴儿装备'],
                seeds: [
                    { zh: '婴儿床', en: 'Baby Crib' },
                    { zh: '餐椅', en: 'Baby High Chair' },
                    { zh: '背带', en: 'Baby Carrier' },
                    { zh: '学步车', en: 'Baby Walker' },
                    { zh: '摇椅', en: 'Baby Bouncer' },
                    { zh: '尿布台', en: 'Changing Table' },
                    { zh: '围栏', en: 'Playpen' },
                    { zh: '浴盆', en: 'Baby Bathtub' },
                ],
            },
            {
                id: 'feeding-care',
                label: '喂养护理',
                en: 'Feeding & Care',
                aliases: ['喂养用品', '哺喂用品', '宝宝护理', '哺乳用品'],
                seeds: [
                    { zh: '奶瓶', en: 'Baby Bottle' },
                    { zh: '吸奶器', en: 'Breast Pump' },
                    { zh: '安抚奶嘴', en: 'Pacifier' },
                    { zh: '辅食碗', en: 'Baby Feeding Bowl' },
                    { zh: '围兜', en: 'Baby Bib' },
                    { zh: '温奶器', en: 'Bottle Warmer' },
                    { zh: '奶瓶刷', en: 'Bottle Brush' },
                    { zh: '吸管杯', en: 'Sippy Cup' },
                ],
            },
            {
                id: 'baby-apparel',
                label: '婴童服饰',
                en: 'Baby Apparel',
                aliases: ['婴儿服装', '童装', '宝宝衣服', '婴童装'],
                seeds: [
                    { zh: '连体衣', en: 'Baby Onesie' },
                    { zh: '睡袋', en: 'Baby Sleep Sack' },
                    { zh: '婴儿帽', en: 'Baby Hat' },
                    { zh: '口水巾', en: 'Baby Drool Bib' },
                    { zh: '婴儿袜', en: 'Baby Socks' },
                    { zh: '爬服', en: 'Baby Romper' },
                    { zh: '包被', en: 'Baby Swaddle Blanket' },
                    { zh: '防踢被', en: 'Toddler Sleep Blanket' },
                ],
            },
            {
                id: 'plush-toys',
                label: '毛绒玩具',
                en: 'Plush Toys',
                aliases: ['毛绒公仔', '玩偶', '布绒玩具', '毛绒玩偶'],
                seeds: [
                    { zh: '毛绒熊', en: 'Plush Teddy Bear' },
                    { zh: '抱枕公仔', en: 'Plush Cushion Toy' },
                    { zh: '安抚玩偶', en: 'Baby Comfort Plush' },
                    { zh: '毛绒挂件', en: 'Plush Keychain' },
                    { zh: '毛绒抱枕', en: 'Plush Pillow' },
                    { zh: '动物玩偶', en: 'Stuffed Animal' },
                    { zh: '手偶', en: 'Plush Hand Puppet' },
                    { zh: '毛绒靠垫', en: 'Plush Back Cushion' },
                ],
            },
            {
                id: 'educational-toys',
                label: '益智玩具',
                en: 'Educational Toys',
                aliases: ['早教玩具', '益智教具', '儿童玩具', '智力玩具'],
                seeds: [
                    { zh: '积木', en: 'Building Blocks' },
                    { zh: '拼图', en: 'Jigsaw Puzzle' },
                    { zh: '串珠', en: 'Lacing Beads' },
                    { zh: '磁力片', en: 'Magnetic Tiles' },
                    { zh: '早教机', en: 'Learning Tablet Toy' },
                    { zh: '蒙氏教具', en: 'Montessori Toy' },
                    { zh: '叠叠杯', en: 'Stacking Cups' },
                    { zh: '形状配对', en: 'Shape Sorter' },
                ],
            },
        ],
    },
    {
        id: 'personal-care-home',
        label: '个护与家清',
        en: 'Personal Care & Home Care',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.teal.hex,
        tone: 'warm',
        segments: [
            {
                id: 'hair-care',
                label: '洗护发',
                en: 'Hair Care',
                aliases: ['洗发水', '护发素', '头发护理', '洗护'],
                seeds: [
                    { zh: '洗发水', en: 'Shampoo' },
                    { zh: '护发素', en: 'Hair Conditioner' },
                    { zh: '发膜', en: 'Hair Mask' },
                    { zh: '护发精油', en: 'Hair Oil Serum' },
                    { zh: '定型发胶', en: 'Hair Styling Gel' },
                    { zh: '染发剂', en: 'Hair Dye' },
                    { zh: '头皮磨砂膏', en: 'Scalp Scrub' },
                    { zh: '免洗喷雾', en: 'Dry Shampoo Spray' },
                ],
            },
            {
                id: 'body-bath',
                label: '沐浴身体',
                en: 'Bath and Body',
                aliases: ['沐浴露', '身体乳', '洗护身体', '浴盐'],
                seeds: [
                    { zh: '沐浴露', en: 'Body Wash' },
                    { zh: '身体乳', en: 'Body Lotion' },
                    { zh: '香皂', en: 'Bar Soap' },
                    { zh: '浴盐', en: 'Bath Salt' },
                    { zh: '磨砂膏', en: 'Body Scrub' },
                    { zh: '护手霜', en: 'Hand Cream' },
                    { zh: '泡澡球', en: 'Bath Bomb' },
                    { zh: '脱毛膏', en: 'Hair Removal Cream' },
                ],
            },
            {
                id: 'oral-care',
                label: '口腔护理',
                en: 'Oral Care',
                aliases: ['牙膏', '牙刷', '口腔清洁', '漱口水'],
                seeds: [
                    { zh: '牙膏', en: 'Toothpaste' },
                    { zh: '牙刷', en: 'Toothbrush' },
                    { zh: '漱口水', en: 'Mouthwash' },
                    { zh: '牙线', en: 'Dental Floss' },
                    { zh: '牙线棒', en: 'Floss Pick' },
                    { zh: '舌苔刷', en: 'Tongue Cleaner' },
                    { zh: '假牙清洁片', en: 'Denture Cleanser Tablet' },
                    { zh: '牙贴', en: 'Teeth Whitening Strip' },
                ],
            },
            {
                id: 'skin-care',
                label: '护肤面霜',
                en: 'Skin Care',
                aliases: ['面霜', '护肤品', '水乳', '精华'],
                seeds: [
                    { zh: '面霜', en: 'Face Cream' },
                    { zh: '洁面乳', en: 'Facial Cleanser' },
                    { zh: '爽肤水', en: 'Toner' },
                    { zh: '精华液', en: 'Face Serum' },
                    { zh: '面膜', en: 'Facial Mask' },
                    { zh: '防晒霜', en: 'Sunscreen Lotion' },
                    { zh: '眼霜', en: 'Eye Cream' },
                    { zh: '卸妆油', en: 'Cleansing Oil' },
                ],
            },
            {
                id: 'home-cleaning',
                label: '家清用品',
                en: 'Home Cleaning',
                aliases: ['清洁剂', '家清', '洗涤用品', '清洁用品'],
                seeds: [
                    { zh: '洗衣液', en: 'Laundry Detergent' },
                    { zh: '洗洁精', en: 'Dish Soap' },
                    { zh: '洁厕剂', en: 'Toilet Bowl Cleaner' },
                    { zh: '油污清洁剂', en: 'Kitchen Degreaser' },
                    { zh: '玻璃清洁剂', en: 'Glass Cleaner' },
                    { zh: '消毒液', en: 'Disinfectant Liquid' },
                    { zh: '地板清洁剂', en: 'Floor Cleaner' },
                    { zh: '衣物柔顺剂', en: 'Fabric Softener' },
                ],
            },
            {
                id: 'paper-care',
                label: '纸品湿巾',
                en: 'Paper and Wipes',
                aliases: ['纸巾', '湿巾', '卫生纸', '纸品'],
                seeds: [
                    { zh: '抽纸', en: 'Facial Tissue' },
                    { zh: '卷纸', en: 'Toilet Paper' },
                    { zh: '湿巾', en: 'Wet Wipe' },
                    { zh: '厨房纸', en: 'Kitchen Paper Towel' },
                    { zh: '手帕纸', en: 'Pocket Tissue' },
                    { zh: '湿厕纸', en: 'Flushable Wet Wipe' },
                    { zh: '棉柔巾', en: 'Cotton Soft Towel' },
                    { zh: '卸妆湿巾', en: 'Makeup Remover Wipe' },
                ],
            },
        ],
    },
    {
        id: 'health-medical',
        label: '健康与医疗',
        en: 'Health & Medical',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.teal.hex,
        tone: 'minimal',
        segments: [
            {
                id: 'diagnostic-devices',
                label: '诊断设备',
                en: 'Diagnostic Equipment',
                aliases: ['检查仪器', '诊断仪器', '检测设备', '医疗诊断'],
                seeds: [
                    { zh: '电子血压计', en: 'Digital Blood Pressure Monitor' },
                    { zh: '血糖仪', en: 'Blood Glucose Meter' },
                    { zh: '听诊器', en: 'Stethoscope' },
                    { zh: '心电图机', en: 'ECG Machine' },
                    { zh: '脉搏血氧仪', en: 'Pulse Oximeter' },
                    { zh: '红外体温计', en: 'Infrared Thermometer' },
                    { zh: '超声诊断仪', en: 'Ultrasound Scanner' },
                    { zh: '检眼镜', en: 'Ophthalmoscope' },
                ],
            },
            {
                id: 'hospital-furniture',
                label: '医疗家具',
                en: 'Hospital Furniture',
                aliases: ['病床', '护理床', '医用床', '病房家具'],
                seeds: [
                    { zh: '护理床', en: 'Hospital Bed' },
                    { zh: '床头柜', en: 'Bedside Cabinet' },
                    { zh: '输液架', en: 'IV Stand' },
                    { zh: '轮椅', en: 'Wheelchair' },
                    { zh: '医用推车', en: 'Medical Cart' },
                    { zh: '屏风', en: 'Privacy Screen' },
                    { zh: '陪护椅', en: 'Companion Chair' },
                    { zh: '担架', en: 'Stretcher' },
                ],
            },
            {
                id: 'surgical-instruments',
                label: '手术器械',
                en: 'Surgical Instruments',
                aliases: ['手术刀', '外科器械', '手术工具', '器械包'],
                seeds: [
                    { zh: '手术刀柄', en: 'Scalpel Handle' },
                    { zh: '止血钳', en: 'Hemostatic Forceps' },
                    { zh: '手术剪', en: 'Surgical Scissors' },
                    { zh: '持针器', en: 'Needle Holder' },
                    { zh: '牵开器', en: 'Retractor' },
                    { zh: '骨凿', en: 'Bone Chisel' },
                    { zh: '缝合针', en: 'Suture Needle' },
                    { zh: '腹腔镜器械', en: 'Laparoscopic Instrument' },
                ],
            },
            {
                id: 'wound-care',
                label: '伤口护理',
                en: 'Wound Care Supplies',
                aliases: ['敷料', '包扎用品', '医用耗材', '创伤护理'],
                seeds: [
                    { zh: '纱布绷带', en: 'Gauze Bandage' },
                    { zh: '创可贴', en: 'Adhesive Bandage' },
                    { zh: '医用胶带', en: 'Medical Tape' },
                    { zh: '无菌敷料', en: 'Sterile Dressing' },
                    { zh: '棉签', en: 'Cotton Swab' },
                    { zh: '医用棉球', en: 'Cotton Ball' },
                    { zh: '弹性绷带', en: 'Elastic Bandage' },
                    { zh: '水胶体敷料', en: 'Hydrocolloid Dressing' },
                ],
            },
            {
                id: 'rehab-mobility',
                label: '康复辅具',
                en: 'Rehabilitation Aids',
                aliases: ['康复器材', '助行器', '理疗设备', '辅助器具'],
                seeds: [
                    { zh: '助行器', en: 'Walking Frame' },
                    { zh: '拐杖', en: 'Crutch' },
                    { zh: '颈托', en: 'Cervical Collar' },
                    { zh: '护腰', en: 'Lumbar Support Belt' },
                    { zh: '握力球', en: 'Hand Grip Ball' },
                    { zh: '康复训练器', en: 'Rehabilitation Trainer' },
                    { zh: '防褥疮垫', en: 'Anti-Bedsore Cushion' },
                    { zh: '踝足矫形器', en: 'Ankle Foot Orthosis' },
                ],
            },
            {
                id: 'disposables',
                label: '一次性耗材',
                en: 'Medical Disposables',
                aliases: ['一次性用品', '医用耗材', '防护用品', '消耗品'],
                seeds: [
                    { zh: '一次性注射器', en: 'Disposable Syringe' },
                    { zh: '输液器', en: 'Infusion Set' },
                    { zh: '医用口罩', en: 'Medical Face Mask' },
                    { zh: '检查手套', en: 'Examination Glove' },
                    { zh: '导尿管', en: 'Urinary Catheter' },
                    { zh: '采血针', en: 'Blood Collection Needle' },
                    { zh: '医用帽', en: 'Surgical Cap' },
                    { zh: '隔离衣', en: 'Isolation Gown' },
                ],
            },
        ],
    },
    {
        id: 'gifts-crafts',
        label: '礼品与工艺品',
        en: 'Gifts & Crafts',
        theme: 'meridian',
        palette: brand_palette_1.BRAND_PALETTES.clay.hex,
        tone: 'warm',
        segments: [
            {
                id: 'promotional-gifts',
                label: '促销礼品',
                en: 'Promotional Gifts',
                aliases: ['广告礼品', '定制礼品', '赠品', '活动礼品'],
                seeds: [
                    { zh: '马克杯', en: 'Ceramic Mug' },
                    { zh: '帆布袋', en: 'Canvas Tote Bag' },
                    { zh: '广告笔', en: 'Promotional Pen' },
                    { zh: '钥匙扣', en: 'Keychain' },
                    { zh: '鼠标垫', en: 'Mouse Pad' },
                    { zh: '保温杯', en: 'Insulated Tumbler' },
                    { zh: 'U盘', en: 'USB Flash Drive' },
                    { zh: '雨伞', en: 'Folding Umbrella' },
                ],
            },
            {
                id: 'crafts-decor',
                label: '工艺摆件',
                en: 'Craft Decorations',
                aliases: ['家居摆件', '装饰工艺品', '工艺礼品', '桌面摆件'],
                seeds: [
                    { zh: '树脂摆件', en: 'Resin Figurine' },
                    { zh: '水晶摆件', en: 'Crystal Ornament' },
                    { zh: '陶瓷花瓶', en: 'Ceramic Vase' },
                    { zh: '木质相框', en: 'Wooden Photo Frame' },
                    { zh: '金属雕塑', en: 'Metal Sculpture' },
                    { zh: '香薰蜡烛', en: 'Scented Candle' },
                    { zh: '沙漏摆件', en: 'Sand Timer Decor' },
                    { zh: '风铃', en: 'Wind Chime' },
                ],
            },
            {
                id: 'festival-supplies',
                label: '节庆用品',
                en: 'Festival Supplies',
                aliases: ['节日装饰', '圣诞用品', '节庆装饰', '派对用品'],
                seeds: [
                    { zh: '圣诞树', en: 'Artificial Christmas Tree' },
                    { zh: '圣诞挂饰', en: 'Christmas Ornament' },
                    { zh: '南瓜灯', en: 'Halloween Lantern' },
                    { zh: '节日彩灯', en: 'Festival String Light' },
                    { zh: '派对气球', en: 'Party Balloon' },
                    { zh: '生日蜡烛', en: 'Birthday Candle' },
                    { zh: '节日面具', en: 'Festival Mask' },
                    { zh: '彩带拉花', en: 'Party Streamer' },
                ],
            },
            {
                id: 'handicraft-textile',
                label: '手工布艺',
                en: 'Handicraft Textiles',
                aliases: ['布艺礼品', '刺绣工艺品', '手工饰品', '布艺家居'],
                seeds: [
                    { zh: '刺绣挂画', en: 'Embroidered Wall Art' },
                    { zh: '布艺收纳盒', en: 'Fabric Storage Box' },
                    { zh: '手工抱枕', en: 'Handmade Cushion Cover' },
                    { zh: '布艺玩偶', en: 'Fabric Doll' },
                    { zh: '刺绣书签', en: 'Embroidered Bookmark' },
                    { zh: '布艺桌旗', en: 'Fabric Table Runner' },
                    { zh: '手工香囊', en: 'Scented Sachet' },
                    { zh: '拼布杯垫', en: 'Patchwork Coaster' },
                ],
            },
            {
                id: 'stationery-craft',
                label: '文具礼品',
                en: 'Stationery Gifts',
                aliases: ['办公礼品', '文创礼品', '文具套装', '学习用品'],
                seeds: [
                    { zh: '笔记本', en: 'Notebook Journal' },
                    { zh: '金属书签', en: 'Metal Bookmark' },
                    { zh: '礼品盒', en: 'Gift Box' },
                    { zh: '钢笔', en: 'Fountain Pen' },
                    { zh: '桌面收纳', en: 'Desk Organizer' },
                    { zh: '便签纸', en: 'Sticky Note Set' },
                    { zh: '文件夹', en: 'Document Folder' },
                    { zh: '台历', en: 'Desk Calendar' },
                ],
            },
            {
                id: 'jewelry-accessories',
                label: '饰品配件',
                en: 'Jewelry Accessories',
                aliases: ['工艺饰品', '时尚饰品', '小饰品', '配饰礼品'],
                seeds: [
                    { zh: '合金项链', en: 'Alloy Necklace' },
                    { zh: '耳环', en: 'Fashion Earrings' },
                    { zh: '手链', en: 'Beaded Bracelet' },
                    { zh: '发饰', en: 'Hair Accessory' },
                    { zh: '胸针', en: 'Brooch Pin' },
                    { zh: '戒指', en: 'Fashion Ring' },
                    { zh: '首饰盒', en: 'Jewelry Box' },
                    { zh: '丝巾', en: 'Silk Scarf' },
                ],
            },
        ],
    },
    {
        id: 'pet-supplies',
        label: '宠物用品',
        en: 'Pet Supplies',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.forest.hex,
        tone: 'warm',
        segments: [
            {
                id: 'pet-bedding',
                label: '宠物窝垫',
                en: 'Pet Beds & Mats',
                aliases: ['宠物床', '狗窝', '猫窝', '睡垫', '垫子'],
                seeds: [
                    { zh: '宠物沙发床', en: 'Pet Sofa Bed' },
                    { zh: '封闭猫窝', en: 'Enclosed Cat Cave' },
                    { zh: '宠物凉席垫', en: 'Cooling Pet Mat' },
                    { zh: '记忆棉狗垫', en: 'Memory Foam Dog Mat' },
                    { zh: '宠物帐篷窝', en: 'Pet Teepee Tent' },
                    { zh: '悬挂吊床', en: 'Pet Hammock Bed' },
                    { zh: '加热宠物垫', en: 'Heated Pet Pad' },
                    { zh: '宠物地垫', en: 'Pet Floor Mat' },
                ],
            },
            {
                id: 'pet-feeding',
                label: '喂食饮水',
                en: 'Feeding & Watering',
                aliases: ['食盆', '饮水器', '喂食器', '水碗', '饭盆'],
                seeds: [
                    { zh: '自动喂食器', en: 'Automatic Pet Feeder' },
                    { zh: '宠物饮水机', en: 'Pet Water Fountain' },
                    { zh: '不锈钢食盆', en: 'Stainless Steel Bowl' },
                    { zh: '慢食碗', en: 'Slow Feeder Bowl' },
                    { zh: '双碗餐架', en: 'Elevated Bowl Stand' },
                    { zh: '折叠旅行碗', en: 'Collapsible Travel Bowl' },
                    { zh: '悬挂水壶', en: 'Hanging Water Bottle' },
                    { zh: '陶瓷猫碗', en: 'Ceramic Cat Bowl' },
                ],
            },
            {
                id: 'pet-toys',
                label: '宠物玩具',
                en: 'Pet Toys',
                aliases: ['狗玩具', '猫玩具', '咬胶', '逗猫棒', '磨牙玩具'],
                seeds: [
                    { zh: '棉绳咬绳', en: 'Cotton Rope Tug Toy' },
                    { zh: '橡胶漏食球', en: 'Rubber Treat Ball' },
                    { zh: '逗猫棒', en: 'Cat Teaser Wand' },
                    { zh: '猫抓球', en: 'Catnip Ball Toy' },
                    { zh: '发声毛绒玩具', en: 'Squeaky Plush Toy' },
                    { zh: '飞盘', en: 'Pet Frisbee' },
                    { zh: '磨牙骨头', en: 'Chew Bone Toy' },
                    { zh: '隧道玩具', en: 'Cat Play Tunnel' },
                ],
            },
            {
                id: 'pet-walking',
                label: '出行牵引',
                en: 'Walking & Travel',
                aliases: ['牵引绳', '项圈', '胸背带', '宠物背包', '外出用品'],
                seeds: [
                    { zh: '尼龙牵引绳', en: 'Nylon Leash' },
                    { zh: '宠物胸背带', en: 'Pet Harness' },
                    { zh: '可伸缩牵引器', en: 'Retractable Leash' },
                    { zh: '宠物项圈', en: 'Pet Collar' },
                    { zh: '宠物背包', en: 'Pet Carrier Backpack' },
                    { zh: '宠物推车', en: 'Pet Stroller' },
                    { zh: '航空箱', en: 'Pet Travel Crate' },
                    { zh: '宠物雨衣', en: 'Pet Raincoat' },
                ],
            },
            {
                id: 'pet-grooming',
                label: '清洁美容',
                en: 'Grooming & Cleaning',
                aliases: ['美容工具', '洗澡用品', '梳子', '清洁用品', '护理用品'],
                seeds: [
                    { zh: '宠物梳子', en: 'Pet Grooming Brush' },
                    { zh: '脱毛梳', en: 'Pet Deshedding Tool' },
                    { zh: '宠物指甲剪', en: 'Pet Nail Clipper' },
                    { zh: '宠物电推剪', en: 'Pet Clipper' },
                    { zh: '宠物浴液', en: 'Pet Shampoo' },
                    { zh: '吸水毛巾', en: 'Pet Drying Towel' },
                    { zh: '猫砂铲', en: 'Cat Litter Scoop' },
                    { zh: '宠物牙刷', en: 'Pet Toothbrush' },
                ],
            },
            {
                id: 'pet-apparel',
                label: '服饰配件',
                en: 'Pet Apparel & Accessories',
                aliases: ['宠物衣服', '狗衣服', '宠物服饰', '配饰', '穿戴用品'],
                seeds: [
                    { zh: '宠物卫衣', en: 'Pet Sweatshirt' },
                    { zh: '宠物雨衣', en: 'Pet Rain Jacket' },
                    { zh: '宠物棉衣', en: 'Pet Padded Coat' },
                    { zh: '宠物连衣裙', en: 'Pet Dress' },
                    { zh: '宠物鞋', en: 'Pet Shoes' },
                    { zh: '宠物围巾', en: 'Pet Bandana' },
                    { zh: '宠物帽子', en: 'Pet Hat' },
                    { zh: '宠物领结', en: 'Pet Bow Tie' },
                ],
            },
        ],
    },
    {
        id: 'school-office',
        label: '文具与办公',
        en: 'School & Office Supplies',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.slate.hex,
        tone: 'minimal',
        segments: [
            {
                id: 'writing-instruments',
                label: '书写工具',
                en: 'Writing Instruments',
                aliases: ['笔类', '写字笔', '制笔', '笔具'],
                seeds: [
                    { zh: '中性笔', en: 'Gel Pen' },
                    { zh: '圆珠笔', en: 'Ballpoint Pen' },
                    { zh: '铅笔', en: 'Wooden Pencil' },
                    { zh: '马克笔', en: 'Marker Pen' },
                    { zh: '荧光笔', en: 'Highlighter Pen' },
                    { zh: '钢笔', en: 'Fountain Pen' },
                    { zh: '白板笔', en: 'Whiteboard Marker' },
                    { zh: '活动铅笔', en: 'Mechanical Pencil' },
                ],
            },
            {
                id: 'paper-notebooks',
                label: '纸品本册',
                en: 'Paper & Notebooks',
                aliases: ['本子', '笔记本', '纸制品', '学生本'],
                seeds: [
                    { zh: '螺旋笔记本', en: 'Spiral Notebook' },
                    { zh: '胶装本', en: 'Glue-Bound Notebook' },
                    { zh: '活页本', en: 'Loose-Leaf Binder Notebook' },
                    { zh: '便签纸', en: 'Sticky Notes' },
                    { zh: '复印纸', en: 'Copy Paper' },
                    { zh: '素描本', en: 'Sketch Book' },
                    { zh: '日记本', en: 'Hardcover Journal' },
                    { zh: '信笺纸', en: 'Letter Pad' },
                ],
            },
            {
                id: 'desk-stationery',
                label: '桌面文具',
                en: 'Desk Stationery',
                aliases: ['办公文具', '桌面用品', '文具杂项', '办公小件'],
                seeds: [
                    { zh: '订书机', en: 'Stapler' },
                    { zh: '打孔机', en: 'Paper Punch' },
                    { zh: '剪刀', en: 'Office Scissors' },
                    { zh: '美工刀', en: 'Utility Knife' },
                    { zh: '胶带座', en: 'Tape Dispenser' },
                    { zh: '笔筒', en: 'Pen Holder' },
                    { zh: '计算器', en: 'Desktop Calculator' },
                    { zh: '起钉器', en: 'Staple Remover' },
                ],
            },
            {
                id: 'filing-storage',
                label: '文件管理',
                en: 'Filing & Storage',
                aliases: ['文件夹', '档案用品', '收纳文具', '文件柜'],
                seeds: [
                    { zh: '档案盒', en: 'Document Box File' },
                    { zh: '抽杆夹', en: 'Report File' },
                    { zh: '资料册', en: 'Presentation Book' },
                    { zh: '拉链袋', en: 'Zipper Pouch' },
                    { zh: '文件盘', en: 'Desktop File Tray' },
                    { zh: '按扣袋', en: 'Button File Bag' },
                    { zh: '名片册', en: 'Business Card Holder Book' },
                    { zh: '风琴包', en: 'Expanding File Folder' },
                ],
            },
            {
                id: 'school-bags',
                label: '书包箱包',
                en: 'School Bags & Cases',
                aliases: ['学生包', '文具袋', '背包', '笔袋'],
                seeds: [
                    { zh: '双肩书包', en: 'School Backpack' },
                    { zh: '笔袋', en: 'Pencil Case' },
                    { zh: '补习袋', en: 'Tote Study Bag' },
                    { zh: '午餐包', en: 'Insulated Lunch Bag' },
                    { zh: '拉杆书包', en: 'Wheeled School Bag' },
                    { zh: '美术袋', en: 'Art Supply Bag' },
                    { zh: '文具盒', en: 'Pencil Box' },
                    { zh: '电脑包', en: 'Laptop Sleeve Bag' },
                ],
            },
            {
                id: 'art-craft',
                label: '美术用品',
                en: 'Art & Craft Supplies',
                aliases: ['画材', '美术文具', '手工材料', '绘画用品'],
                seeds: [
                    { zh: '水彩笔', en: 'Watercolor Pen' },
                    { zh: '油画棒', en: 'Oil Pastel' },
                    { zh: '彩色铅笔', en: 'Colored Pencil' },
                    { zh: '水彩颜料', en: 'Watercolor Paint Set' },
                    { zh: '调色盘', en: 'Paint Palette' },
                    { zh: '画架', en: 'Artist Easel' },
                    { zh: '橡皮泥', en: 'Modeling Clay' },
                    { zh: '手工剪刀', en: 'Craft Scissors' },
                ],
            },
        ],
    },
    {
        id: 'industrial-machinery',
        label: '工业机械',
        en: 'Industrial Machinery',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.charcoal.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'metal-cutting',
                label: '金属切削',
                en: 'Metal Cutting Machines',
                aliases: ['机床', '切削设备', '加工机床', '机加工设备'],
                seeds: [
                    { zh: '数控车床', en: 'CNC Lathe' },
                    { zh: '立式加工中心', en: 'Vertical Machining Center' },
                    { zh: '摇臂钻床', en: 'Radial Drilling Machine' },
                    { zh: '平面磨床', en: 'Surface Grinding Machine' },
                    { zh: '带锯床', en: 'Band Saw Machine' },
                    { zh: '龙门铣床', en: 'Gantry Milling Machine' },
                    { zh: '电火花机床', en: 'EDM Machine' },
                    { zh: '数控折弯机', en: 'CNC Press Brake' },
                ],
            },
            {
                id: 'forming-press',
                label: '锻压成型',
                en: 'Press and Forming Machines',
                aliases: ['锻压设备', '冲压设备', '成型机械', '压力机'],
                seeds: [
                    { zh: '冲床', en: 'Power Press' },
                    { zh: '液压机', en: 'Hydraulic Press' },
                    { zh: '剪板机', en: 'Shearing Machine' },
                    { zh: '卷板机', en: 'Plate Rolling Machine' },
                    { zh: '空气锤', en: 'Pneumatic Forging Hammer' },
                    { zh: '摩擦压力机', en: 'Friction Screw Press' },
                    { zh: '开卷校平线', en: 'Cut-to-Length Line' },
                    { zh: '冷弯成型机', en: 'Roll Forming Machine' },
                ],
            },
            {
                id: 'plastic-rubber',
                label: '塑胶机械',
                en: 'Plastic and Rubber Machinery',
                aliases: ['塑料机械', '注塑设备', '橡塑机械', '塑机'],
                seeds: [
                    { zh: '注塑机', en: 'Injection Molding Machine' },
                    { zh: '挤出机', en: 'Plastic Extruder' },
                    { zh: '吹膜机', en: 'Film Blowing Machine' },
                    { zh: '吹瓶机', en: 'Bottle Blowing Machine' },
                    { zh: '密炼机', en: 'Internal Mixer' },
                    { zh: '开炼机', en: 'Two-Roll Mill' },
                    { zh: '吸塑机', en: 'Thermoforming Machine' },
                    { zh: '破碎机', en: 'Plastic Crusher' },
                ],
            },
            {
                id: 'packaging-machinery',
                label: '包装机械',
                en: 'Packaging Machinery',
                aliases: ['包装设备', '灌装设备', '封口机械', '包装机'],
                seeds: [
                    { zh: '灌装机', en: 'Filling Machine' },
                    { zh: '封口机', en: 'Sealing Machine' },
                    { zh: '贴标机', en: 'Labeling Machine' },
                    { zh: '枕式包装机', en: 'Flow Wrapping Machine' },
                    { zh: '真空包装机', en: 'Vacuum Packaging Machine' },
                    { zh: '收缩包装机', en: 'Shrink Wrapping Machine' },
                    { zh: '封箱机', en: 'Carton Sealing Machine' },
                    { zh: '码垛机', en: 'Palletizing Machine' },
                ],
            },
            {
                id: 'lifting-conveying',
                label: '起重输送',
                en: 'Lifting and Conveying Equipment',
                aliases: ['输送设备', '起重机械', '搬运设备', '提升机'],
                seeds: [
                    { zh: '皮带输送机', en: 'Belt Conveyor' },
                    { zh: '螺旋输送机', en: 'Screw Conveyor' },
                    { zh: '斗式提升机', en: 'Bucket Elevator' },
                    { zh: '电动葫芦', en: 'Electric Hoist' },
                    { zh: '桥式起重机', en: 'Overhead Crane' },
                    { zh: '叉车', en: 'Forklift Truck' },
                    { zh: '滚筒输送线', en: 'Roller Conveyor Line' },
                    { zh: '振动给料机', en: 'Vibrating Feeder' },
                ],
            },
            {
                id: 'general-components',
                label: '通用配件',
                en: 'General Machinery Components',
                aliases: ['机械配件', '传动件', '工业零部件', '通用件'],
                seeds: [
                    { zh: '减速机', en: 'Gear Reducer' },
                    { zh: '联轴器', en: 'Shaft Coupling' },
                    { zh: '轴承座', en: 'Bearing Housing' },
                    { zh: '液压缸', en: 'Hydraulic Cylinder' },
                    { zh: '气缸', en: 'Pneumatic Cylinder' },
                    { zh: '链条', en: 'Roller Chain' },
                    { zh: '皮带轮', en: 'V-Belt Pulley' },
                    { zh: '直线导轨', en: 'Linear Guide Rail' },
                ],
            },
        ],
    },
    {
        id: 'commercial-equipment',
        label: '商用设备',
        en: 'Commercial Equipment & Machinery',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.steel.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'commercial-refrigeration',
                label: '商用制冷',
                en: 'Commercial Refrigeration',
                aliases: ['冷冻设备', '冷藏柜', '制冷设备', '冰柜'],
                seeds: [
                    { zh: '四门冰箱', en: 'Four Door Refrigerator' },
                    { zh: '冷藏工作台', en: 'Refrigerated Work Table' },
                    { zh: '制冰机', en: 'Ice Maker Machine' },
                    { zh: '冷库', en: 'Cold Storage Room' },
                    { zh: '蛋糕展示柜', en: 'Cake Display Cabinet' },
                    { zh: '饮料柜', en: 'Beverage Cooler' },
                    { zh: '速冻柜', en: 'Blast Freezer' },
                    { zh: '冰淇淋机', en: 'Ice Cream Machine' },
                ],
            },
            {
                id: 'food-preparation',
                label: '食品加工',
                en: 'Food Preparation Equipment',
                aliases: ['厨房设备', '加工机械', '食品机械', '厨房机械'],
                seeds: [
                    { zh: '和面机', en: 'Dough Mixer' },
                    { zh: '绞肉机', en: 'Meat Grinder' },
                    { zh: '切片机', en: 'Meat Slicer' },
                    { zh: '压面机', en: 'Noodle Press Machine' },
                    { zh: '搅拌机', en: 'Planetary Mixer' },
                    { zh: '切菜机', en: 'Vegetable Cutting Machine' },
                    { zh: '榨汁机', en: 'Juice Extractor' },
                    { zh: '包子机', en: 'Steamed Bun Making Machine' },
                ],
            },
            {
                id: 'cooking-equipment',
                label: '烹饪炉灶',
                en: 'Commercial Cooking Equipment',
                aliases: ['炉灶', '炊事设备', '厨房炉具', '灶具'],
                seeds: [
                    { zh: '燃气炒灶', en: 'Gas Wok Range' },
                    { zh: '蒸饭车', en: 'Rice Steamer Cart' },
                    { zh: '电烤箱', en: 'Commercial Oven' },
                    { zh: '炸炉', en: 'Deep Fryer' },
                    { zh: '扒炉', en: 'Griddle Plate' },
                    { zh: '煮面炉', en: 'Noodle Boiler' },
                    { zh: '保温餐车', en: 'Food Holding Cart' },
                    { zh: '微波炉', en: 'Commercial Microwave Oven' },
                ],
            },
            {
                id: 'warewashing',
                label: '洗涤消毒',
                en: 'Warewashing Equipment',
                aliases: ['洗碗设备', '清洗设备', '消毒柜', '洗碗机'],
                seeds: [
                    { zh: '揭盖洗碗机', en: 'Hood Type Dishwasher' },
                    { zh: '通道洗碗机', en: 'Conveyor Dishwasher' },
                    { zh: '消毒柜', en: 'Sterilizing Cabinet' },
                    { zh: '餐具保洁柜', en: 'Dish Storage Cabinet' },
                    { zh: '高压喷淋机', en: 'High Pressure Washer' },
                    { zh: '洗杯机', en: 'Glass Washer' },
                    { zh: '沥水工作台', en: 'Drain Work Table' },
                    { zh: '垃圾处理器', en: 'Food Waste Disposer' },
                ],
            },
            {
                id: 'laundry-equipment',
                label: '洗衣设备',
                en: 'Commercial Laundry Equipment',
                aliases: ['洗涤机械', '洗衣房设备', '干洗设备', '水洗设备'],
                seeds: [
                    { zh: '工业洗衣机', en: 'Industrial Washing Machine' },
                    { zh: '工业烘干机', en: 'Industrial Tumble Dryer' },
                    { zh: '烫平机', en: 'Flatwork Ironer' },
                    { zh: '干洗机', en: 'Dry Cleaning Machine' },
                    { zh: '折叠机', en: 'Folding Machine' },
                    { zh: '脱水机', en: 'Hydro Extractor' },
                    { zh: '熨烫台', en: 'Ironing Table' },
                    { zh: '洗衣房推车', en: 'Laundry Trolley' },
                ],
            },
            {
                id: 'store-hotel-fixtures',
                label: '商超酒店',
                en: 'Store and Hotel Fixtures',
                aliases: ['货架收银', '酒店用品', '商超设备', '陈列设备'],
                seeds: [
                    { zh: '超市货架', en: 'Supermarket Shelf' },
                    { zh: '收银台', en: 'Checkout Counter' },
                    { zh: '购物车', en: 'Shopping Trolley' },
                    { zh: '收银机', en: 'Cash Register' },
                    { zh: '电子秤', en: 'Electronic Scale' },
                    { zh: '行李车', en: 'Luggage Cart' },
                    { zh: '客房冰箱', en: 'Minibar Fridge' },
                    { zh: '保险箱', en: 'Hotel Safe' },
                ],
            },
        ],
    },
    {
        id: 'construction-machinery',
        label: '工程机械',
        en: 'Construction & Building Machinery',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.sienna.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'earthmoving',
                label: '土方机械',
                en: 'Earthmoving Machinery',
                aliases: ['挖掘设备', '土石方机械', '铲土机械', '挖机'],
                seeds: [
                    { zh: '挖掘机', en: 'Hydraulic Excavator' },
                    { zh: '装载机', en: 'Wheel Loader' },
                    { zh: '推土机', en: 'Bulldozer' },
                    { zh: '平地机', en: 'Motor Grader' },
                    { zh: '滑移装载机', en: 'Skid Steer Loader' },
                    { zh: '挖掘装载机', en: 'Backhoe Loader' },
                    { zh: '履带运输车', en: 'Crawler Carrier' },
                    { zh: '铲运机', en: 'Scraper' },
                ],
            },
            {
                id: 'lifting',
                label: '起重机械',
                en: 'Lifting Equipment',
                aliases: ['吊装设备', '起重设备', '吊车', '升降机械'],
                seeds: [
                    { zh: '汽车起重机', en: 'Truck Crane' },
                    { zh: '塔式起重机', en: 'Tower Crane' },
                    { zh: '履带起重机', en: 'Crawler Crane' },
                    { zh: '随车吊', en: 'Truck Mounted Crane' },
                    { zh: '电动葫芦', en: 'Electric Hoist' },
                    { zh: '桥式起重机', en: 'Overhead Crane' },
                    { zh: '施工升降机', en: 'Construction Hoist' },
                    { zh: '高空作业车', en: 'Aerial Work Platform' },
                ],
            },
            {
                id: 'concrete',
                label: '混凝土机械',
                en: 'Concrete Machinery',
                aliases: ['砼机械', '混凝土设备', '搅拌设备', '泵送设备'],
                seeds: [
                    { zh: '混凝土搅拌站', en: 'Concrete Batching Plant' },
                    { zh: '混凝土泵车', en: 'Concrete Pump Truck' },
                    { zh: '拖泵', en: 'Trailer Concrete Pump' },
                    { zh: '搅拌运输车', en: 'Concrete Mixer Truck' },
                    { zh: '强制搅拌机', en: 'Twin Shaft Mixer' },
                    { zh: '混凝土喷射机', en: 'Shotcrete Machine' },
                    { zh: '振捣棒', en: 'Concrete Vibrator' },
                    { zh: '布料机', en: 'Concrete Placing Boom' },
                ],
            },
            {
                id: 'road-paving',
                label: '路面机械',
                en: 'Road Construction Machinery',
                aliases: ['筑路机械', '道路机械', '摊铺设备', '压实机械'],
                seeds: [
                    { zh: '压路机', en: 'Road Roller' },
                    { zh: '沥青摊铺机', en: 'Asphalt Paver' },
                    { zh: '铣刨机', en: 'Cold Milling Machine' },
                    { zh: '稳定土拌和站', en: 'Stabilized Soil Mixing Plant' },
                    { zh: '沥青搅拌站', en: 'Asphalt Mixing Plant' },
                    { zh: '沥青洒布车', en: 'Asphalt Distributor Truck' },
                    { zh: '路面切割机', en: 'Road Cutter' },
                    { zh: '冲击夯', en: 'Vibratory Rammer' },
                ],
            },
            {
                id: 'drilling-piling',
                label: '桩工钻机',
                en: 'Piling And Drilling Machinery',
                aliases: ['桩工机械', '钻孔设备', '打桩设备', '基础施工机械'],
                seeds: [
                    { zh: '旋挖钻机', en: 'Rotary Drilling Rig' },
                    { zh: '液压打桩锤', en: 'Hydraulic Pile Hammer' },
                    { zh: '长螺旋钻机', en: 'Continuous Flight Auger Drill' },
                    { zh: '静压桩机', en: 'Static Pile Driver' },
                    { zh: '潜孔钻机', en: 'Down The Hole Drill Rig' },
                    { zh: '锚杆钻机', en: 'Anchor Drilling Rig' },
                    { zh: '地下连续墙抓斗', en: 'Trench Cutter' },
                    { zh: '水井钻机', en: 'Water Well Drilling Rig' },
                ],
            },
            {
                id: 'attachments-parts',
                label: '属具配件',
                en: 'Attachments And Spare Parts',
                aliases: ['工程属具', '挖机配件', '机械配件', '工作装置'],
                seeds: [
                    { zh: '液压破碎锤', en: 'Hydraulic Breaker' },
                    { zh: '挖斗', en: 'Excavator Bucket' },
                    { zh: '快换接头', en: 'Quick Coupler' },
                    { zh: '抓木器', en: 'Log Grapple' },
                    { zh: '液压剪', en: 'Hydraulic Shear' },
                    { zh: '履带板', en: 'Track Shoe' },
                    { zh: '支重轮', en: 'Track Roller' },
                    { zh: '铲刀刃板', en: 'Cutting Edge' },
                ],
            },
        ],
    },
    {
        id: 'construction-realestate',
        label: '建材与房产',
        en: 'Construction & Real Estate',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.stone.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'structural-materials',
                label: '结构建材',
                en: 'Structural Building Materials',
                aliases: ['主体材料', '土建材料', '钢材水泥', '基础建材'],
                seeds: [
                    { zh: '螺纹钢', en: 'Deformed Steel Rebar' },
                    { zh: '热轧卷板', en: 'Hot Rolled Steel Coil' },
                    { zh: '硅酸盐水泥', en: 'Portland Cement' },
                    { zh: '加气混凝土砌块', en: 'AAC Block' },
                    { zh: '预应力管桩', en: 'Prestressed Concrete Pile' },
                    { zh: '工字钢', en: 'H Beam Steel' },
                    { zh: '彩钢夹芯板', en: 'Sandwich Panel' },
                    { zh: '建筑模板', en: 'Film Faced Plywood' },
                ],
            },
            {
                id: 'wall-roof',
                label: '墙屋面',
                en: 'Wall and Roof Systems',
                aliases: ['围护材料', '屋面瓦', '墙体材料', '外墙板'],
                seeds: [
                    { zh: '彩钢瓦', en: 'Corrugated Steel Sheet' },
                    { zh: '铝塑板', en: 'Aluminum Composite Panel' },
                    { zh: '石膏板', en: 'Gypsum Board' },
                    { zh: '陶土瓦', en: 'Clay Roof Tile' },
                    { zh: '岩棉夹芯板', en: 'Rock Wool Sandwich Panel' },
                    { zh: '纤维水泥板', en: 'Fiber Cement Board' },
                    { zh: '轻钢龙骨', en: 'Steel Stud' },
                    { zh: 'PVC吊顶板', en: 'PVC Ceiling Panel' },
                ],
            },
            {
                id: 'flooring-tiles',
                label: '地面瓷砖',
                en: 'Flooring and Tiles',
                aliases: ['地砖', '地板', '石材', '铺地材料'],
                seeds: [
                    { zh: '抛光砖', en: 'Polished Porcelain Tile' },
                    { zh: '釉面内墙砖', en: 'Ceramic Wall Tile' },
                    { zh: '强化木地板', en: 'Laminate Flooring' },
                    { zh: 'SPC石塑地板', en: 'SPC Vinyl Flooring' },
                    { zh: '花岗岩板材', en: 'Granite Slab' },
                    { zh: '大理石台面', en: 'Marble Countertop' },
                    { zh: '防静电地板', en: 'Raised Access Floor' },
                    { zh: '橡胶地垫', en: 'Rubber Floor Mat' },
                ],
            },
            {
                id: 'doors-windows',
                label: '门窗',
                en: 'Doors and Windows',
                aliases: ['铝合金门窗', '建筑门窗', '入户门', '幕墙'],
                seeds: [
                    { zh: '断桥铝窗', en: 'Thermal Break Aluminum Window' },
                    { zh: '塑钢窗', en: 'UPVC Window' },
                    { zh: '钢质防火门', en: 'Steel Fire Door' },
                    { zh: '实木复合门', en: 'Wood Composite Door' },
                    { zh: '卷帘门', en: 'Rolling Shutter Door' },
                    { zh: '玻璃幕墙', en: 'Glass Curtain Wall' },
                    { zh: '铝合金推拉门', en: 'Aluminum Sliding Door' },
                    { zh: '车库门', en: 'Garage Door' },
                ],
            },
            {
                id: 'plumbing-mep',
                label: '水电管材',
                en: 'Plumbing and MEP',
                aliases: ['给排水', '管材管件', '水电材料', '暖通'],
                seeds: [
                    { zh: 'PPR管', en: 'PPR Pipe' },
                    { zh: 'PVC排水管', en: 'PVC Drainage Pipe' },
                    { zh: '镀锌钢管', en: 'Galvanized Steel Pipe' },
                    { zh: 'PE给水管', en: 'PE Water Pipe' },
                    { zh: '铜阀门', en: 'Brass Valve' },
                    { zh: '地暖管', en: 'Underfloor Heating Pipe' },
                    { zh: '不锈钢软管', en: 'Braided Hose' },
                    { zh: '铸铁排水管', en: 'Cast Iron Drain Pipe' },
                ],
            },
            {
                id: 'hardware-fittings',
                label: '五金配件',
                en: 'Building Hardware',
                aliases: ['建筑五金', '门窗五金', '紧固件', '小五金'],
                seeds: [
                    { zh: '合页', en: 'Door Hinge' },
                    { zh: '门锁', en: 'Door Lock' },
                    { zh: '膨胀螺栓', en: 'Expansion Bolt' },
                    { zh: '自攻螺丝', en: 'Self Tapping Screw' },
                    { zh: '玻璃胶', en: 'Silicone Sealant' },
                    { zh: '拉手', en: 'Door Handle' },
                    { zh: '闭门器', en: 'Door Closer' },
                    { zh: '角码', en: 'Corner Bracket' },
                ],
            },
        ],
    },
    {
        id: 'furniture',
        label: '家具',
        en: 'Furniture',
        theme: 'meridian',
        palette: brand_palette_1.BRAND_PALETTES.stone.hex,
        tone: 'warm',
        segments: [
            {
                id: 'living-room',
                label: '客厅家具',
                en: 'Living Room Furniture',
                aliases: ['软体家具', '沙发茶几', '客厅套装', '民用家具'],
                seeds: [
                    { zh: '布艺沙发', en: 'Fabric Sofa' },
                    { zh: '真皮沙发', en: 'Leather Sofa' },
                    { zh: '茶几', en: 'Coffee Table' },
                    { zh: '电视柜', en: 'TV Stand' },
                    { zh: '休闲椅', en: 'Accent Chair' },
                    { zh: '边几', en: 'Side Table' },
                    { zh: '鞋柜', en: 'Shoe Cabinet' },
                    { zh: '沙发床', en: 'Sofa Bed' },
                ],
            },
            {
                id: 'dining-room',
                label: '餐厅家具',
                en: 'Dining Room Furniture',
                aliases: ['餐台椅', '餐桌椅', '餐厅套装', '饭厅家具'],
                seeds: [
                    { zh: '实木餐桌', en: 'Solid Wood Dining Table' },
                    { zh: '餐椅', en: 'Dining Chair' },
                    { zh: '餐边柜', en: 'Sideboard Cabinet' },
                    { zh: '酒柜', en: 'Wine Cabinet' },
                    { zh: '吧台椅', en: 'Bar Stool' },
                    { zh: '折叠餐桌', en: 'Folding Dining Table' },
                    { zh: '大理石餐台', en: 'Marble Dining Table' },
                    { zh: '卡座沙发', en: 'Banquette Seating' },
                ],
            },
            {
                id: 'bedroom',
                label: '卧室家具',
                en: 'Bedroom Furniture',
                aliases: ['卧房家具', '床组', '套房家具', '寝具家具'],
                seeds: [
                    { zh: '实木床', en: 'Wooden Bed Frame' },
                    { zh: '软床', en: 'Upholstered Bed' },
                    { zh: '床头柜', en: 'Nightstand' },
                    { zh: '衣柜', en: 'Wardrobe' },
                    { zh: '梳妆台', en: 'Dressing Table' },
                    { zh: '五斗柜', en: 'Chest of Drawers' },
                    { zh: '上下床', en: 'Bunk Bed' },
                    { zh: '床垫', en: 'Mattress' },
                ],
            },
            {
                id: 'office-furniture',
                label: '办公家具',
                en: 'Office Furniture',
                aliases: ['商用家具', '办公桌椅', '职员家具', '办公套装'],
                seeds: [
                    { zh: '办公桌', en: 'Office Desk' },
                    { zh: '办公椅', en: 'Office Chair' },
                    { zh: '会议桌', en: 'Conference Table' },
                    { zh: '文件柜', en: 'Filing Cabinet' },
                    { zh: '办公屏风', en: 'Office Partition' },
                    { zh: '升降桌', en: 'Height Adjustable Desk' },
                    { zh: '培训桌', en: 'Training Table' },
                    { zh: '前台', en: 'Reception Desk' },
                ],
            },
            {
                id: 'outdoor-furniture',
                label: '户外家具',
                en: 'Outdoor Furniture',
                aliases: ['庭院家具', '花园家具', '休闲户外', '藤编家具'],
                seeds: [
                    { zh: '藤编沙发', en: 'Rattan Sofa Set' },
                    { zh: '户外躺椅', en: 'Outdoor Lounge Chair' },
                    { zh: '折叠桌椅', en: 'Folding Table and Chair' },
                    { zh: '秋千椅', en: 'Swing Chair' },
                    { zh: '遮阳伞', en: 'Patio Umbrella' },
                    { zh: '户外餐桌', en: 'Outdoor Dining Table' },
                    { zh: '吊篮椅', en: 'Hanging Egg Chair' },
                    { zh: '沙滩椅', en: 'Beach Chair' },
                ],
            },
            {
                id: 'commercial-furniture',
                label: '商用家具',
                en: 'Commercial Furniture',
                aliases: ['酒店家具', '工程家具', '定制家具', '公共家具'],
                seeds: [
                    { zh: '酒店客房床', en: 'Hotel Bed' },
                    { zh: '餐厅卡座', en: 'Restaurant Booth' },
                    { zh: '学校课桌', en: 'School Desk' },
                    { zh: '医院陪护椅', en: 'Hospital Companion Chair' },
                    { zh: '酒吧吧台', en: 'Bar Counter' },
                    { zh: '礼堂座椅', en: 'Auditorium Seating' },
                    { zh: '实验室工作台', en: 'Lab Workbench' },
                    { zh: '储物货架', en: 'Storage Shelving' },
                ],
            },
        ],
    },
    {
        id: 'lighting',
        label: '灯具照明',
        en: 'Lights & Lighting',
        theme: 'nocturne',
        palette: brand_palette_1.BRAND_PALETTES.slate.hex,
        tone: 'minimal',
        segments: [
            {
                id: 'indoor-lighting',
                label: '室内照明',
                en: 'Indoor Lighting',
                aliases: ['家居灯', '室内灯', '家用灯具', '室内灯具'],
                seeds: [
                    { zh: '吸顶灯', en: 'Ceiling Light' },
                    { zh: '吊灯', en: 'Pendant Light' },
                    { zh: '筒灯', en: 'Recessed Downlight' },
                    { zh: '轨道灯', en: 'Track Light' },
                    { zh: '壁灯', en: 'Wall Sconce' },
                    { zh: '台灯', en: 'Table Lamp' },
                    { zh: '落地灯', en: 'Floor Lamp' },
                    { zh: '镜前灯', en: 'Vanity Light' },
                ],
            },
            {
                id: 'outdoor-lighting',
                label: '户外照明',
                en: 'Outdoor Lighting',
                aliases: ['室外灯', '户外灯', '景观灯', '庭院灯'],
                seeds: [
                    { zh: '路灯', en: 'Street Light' },
                    { zh: '投光灯', en: 'Flood Light' },
                    { zh: '庭院灯', en: 'Garden Light' },
                    { zh: '草坪灯', en: 'Lawn Light' },
                    { zh: '地埋灯', en: 'Inground Light' },
                    { zh: '壁挂灯', en: 'Wall Mounted Light' },
                    { zh: '太阳能路灯', en: 'Solar Street Light' },
                    { zh: '水下灯', en: 'Underwater Light' },
                ],
            },
            {
                id: 'commercial-lighting',
                label: '商业照明',
                en: 'Commercial Lighting',
                aliases: ['工程灯', '商照', '店铺灯', '商业灯具'],
                seeds: [
                    { zh: '面板灯', en: 'LED Panel Light' },
                    { zh: '灯盘', en: 'Troffer Light' },
                    { zh: '线条灯', en: 'Linear Light' },
                    { zh: '工矿灯', en: 'High Bay Light' },
                    { zh: '防爆灯', en: 'Explosion Proof Light' },
                    { zh: '净化灯', en: 'Clean Room Light' },
                    { zh: '应急灯', en: 'Emergency Light' },
                    { zh: '支架灯', en: 'Bathtub Light' },
                ],
            },
            {
                id: 'decorative-lighting',
                label: '装饰灯',
                en: 'Decorative Lighting',
                aliases: ['氛围灯', '装饰灯具', '灯饰', '花灯'],
                seeds: [
                    { zh: '灯串', en: 'String Light' },
                    { zh: '霓虹灯', en: 'Neon Light' },
                    { zh: '星空灯', en: 'Star Projector Light' },
                    { zh: '蜡烛灯', en: 'Candle Light' },
                    { zh: '水晶灯', en: 'Crystal Chandelier' },
                    { zh: '铁艺灯', en: 'Wrought Iron Light' },
                    { zh: '纸灯笼', en: 'Paper Lantern' },
                    { zh: '节日灯', en: 'Holiday Light' },
                ],
            },
            {
                id: 'light-source',
                label: '光源',
                en: 'Light Sources',
                aliases: ['灯泡', '灯管', '光源产品', '电光源'],
                seeds: [
                    { zh: 'LED球泡', en: 'LED Bulb' },
                    { zh: '灯管', en: 'LED Tube' },
                    { zh: '射灯杯', en: 'MR16 Spotlight' },
                    { zh: '蜡烛泡', en: 'Candle Bulb' },
                    { zh: '玉米灯', en: 'Corn Bulb' },
                    { zh: '环形灯管', en: 'Circular Tube' },
                    { zh: '卤素灯', en: 'Halogen Lamp' },
                    { zh: '植物生长灯', en: 'Grow Light' },
                ],
            },
            {
                id: 'lighting-accessories',
                label: '灯具配件',
                en: 'Lighting Accessories',
                aliases: ['灯配', '照明配件', '灯饰配件'],
                seeds: [
                    { zh: '灯罩', en: 'Lamp Shade' },
                    { zh: '灯座', en: 'Lamp Holder' },
                    { zh: '驱动电源', en: 'LED Driver' },
                    { zh: '灯头', en: 'Lamp Socket' },
                    { zh: '反光杯', en: 'Reflector Cup' },
                    { zh: '铝基板', en: 'Aluminum PCB' },
                    { zh: '灯臂', en: 'Lamp Arm' },
                    { zh: '吊线', en: 'Suspension Wire' },
                ],
            },
        ],
    },
    {
        id: 'home-appliances',
        label: '家用电器',
        en: 'Home Appliances',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.steel.hex,
        tone: 'tech',
        segments: [
            {
                id: 'kitchen-appliances',
                label: '厨房电器',
                en: 'Kitchen Appliances',
                aliases: ['小家电', '厨房小电', '烹饪电器', '厨电'],
                seeds: [
                    { zh: '电饭煲', en: 'Electric Rice Cooker' },
                    { zh: '电热水壶', en: 'Electric Kettle' },
                    { zh: '微波炉', en: 'Microwave Oven' },
                    { zh: '电磁炉', en: 'Induction Cooktop' },
                    { zh: '空气炸锅', en: 'Air Fryer' },
                    { zh: '豆浆机', en: 'Soy Milk Maker' },
                    { zh: '榨汁机', en: 'Juice Extractor' },
                    { zh: '电烤箱', en: 'Electric Oven' },
                ],
            },
            {
                id: 'home-cleaning',
                label: '清洁电器',
                en: 'Cleaning Appliances',
                aliases: ['吸尘器', '除尘电器', '地面清洁', '清洁小电'],
                seeds: [
                    { zh: '吸尘器', en: 'Vacuum Cleaner' },
                    { zh: '扫地机器人', en: 'Robot Vacuum' },
                    { zh: '洗地机', en: 'Floor Washer' },
                    { zh: '蒸汽拖把', en: 'Steam Mop' },
                    { zh: '除螨仪', en: 'Mite Remover Vacuum' },
                    { zh: '高压清洗机', en: 'Pressure Washer' },
                    { zh: '空气净化器', en: 'Air Purifier' },
                    { zh: '加湿器', en: 'Humidifier' },
                ],
            },
            {
                id: 'climate-comfort',
                label: '环境电器',
                en: 'Climate Comfort Appliances',
                aliases: ['温控电器', '舒适家电', '冷暖电器', '环境小电'],
                seeds: [
                    { zh: '电风扇', en: 'Electric Fan' },
                    { zh: '冷风扇', en: 'Evaporative Air Cooler' },
                    { zh: '取暖器', en: 'Space Heater' },
                    { zh: '电热毯', en: 'Electric Blanket' },
                    { zh: '除湿机', en: 'Dehumidifier' },
                    { zh: '移动空调', en: 'Portable Air Conditioner' },
                    { zh: '新风系统', en: 'Fresh Air Ventilator' },
                    { zh: '暖风机', en: 'Fan Heater' },
                ],
            },
            {
                id: 'laundry-care',
                label: '洗护电器',
                en: 'Laundry Care Appliances',
                aliases: ['洗衣电器', '衣物护理', '洗涤电器', '洗护家电'],
                seeds: [
                    { zh: '洗衣机', en: 'Washing Machine' },
                    { zh: '干衣机', en: 'Clothes Dryer' },
                    { zh: '挂烫机', en: 'Garment Steamer' },
                    { zh: '电熨斗', en: 'Electric Iron' },
                    { zh: '洗鞋机', en: 'Shoe Washing Machine' },
                    { zh: '脱水机', en: 'Spin Dryer' },
                    { zh: '衣物护理机', en: 'Clothes Care Cabinet' },
                    { zh: '熨烫台', en: 'Ironing Station' },
                ],
            },
            {
                id: 'water-heating',
                label: '热水电器',
                en: 'Water Heating Appliances',
                aliases: ['热水器', '卫浴电器', '洗浴电器', '水家电'],
                seeds: [
                    { zh: '电热水器', en: 'Electric Water Heater' },
                    { zh: '燃气热水器', en: 'Gas Water Heater' },
                    { zh: '空气能热水器', en: 'Heat Pump Water Heater' },
                    { zh: '即热式热水器', en: 'Tankless Water Heater' },
                    { zh: '太阳能热水器', en: 'Solar Water Heater' },
                    { zh: '开水器', en: 'Water Boiler' },
                    { zh: '饮水机', en: 'Water Dispenser' },
                    { zh: '净水器', en: 'Water Purifier' },
                ],
            },
            {
                id: 'personal-care',
                label: '个护电器',
                en: 'Personal Care Appliances',
                aliases: ['美容电器', '护理小电', '个护小家电', '美发电器'],
                seeds: [
                    { zh: '电吹风', en: 'Hair Dryer' },
                    { zh: '剃须刀', en: 'Electric Shaver' },
                    { zh: '电动牙刷', en: 'Electric Toothbrush' },
                    { zh: '卷发棒', en: 'Hair Curler' },
                    { zh: '直发器', en: 'Hair Straightener' },
                    { zh: '美容仪', en: 'Facial Beauty Device' },
                    { zh: '按摩仪', en: 'Massage Device' },
                    { zh: '电动推剪', en: 'Hair Clipper' },
                ],
            },
        ],
    },
    {
        id: 'automotive-tools',
        label: '汽保工具',
        en: 'Automotive Supplies & Tools',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.charcoal.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'diagnostic-tools',
                label: '检测诊断',
                en: 'Diagnostic Tools',
                aliases: ['汽车检测仪', '诊断设备', '检测工具', '汽车诊断'],
                seeds: [
                    { zh: '胎压计', en: 'Tire Pressure Gauge' },
                    { zh: '万用表', en: 'Automotive Multimeter' },
                    { zh: '示波器', en: 'Automotive Oscilloscope' },
                    { zh: '内窥镜', en: 'Borescope Camera' },
                    { zh: '电瓶检测仪', en: 'Battery Tester' },
                    { zh: '测温枪', en: 'Infrared Thermometer' },
                    { zh: '四轮定位仪', en: 'Wheel Alignment Machine' },
                    { zh: '故障诊断仪', en: 'OBD Scanner' },
                ],
            },
            {
                id: 'hand-tools',
                label: '汽修手工具',
                en: 'Hand Tools',
                aliases: ['维修工具', '手动工具', '汽保工具', '修车工具'],
                seeds: [
                    { zh: '套筒扳手', en: 'Socket Wrench Set' },
                    { zh: '扭力扳手', en: 'Torque Wrench' },
                    { zh: '棘轮扳手', en: 'Ratchet Wrench' },
                    { zh: '拉马', en: 'Bearing Puller' },
                    { zh: '卡簧钳', en: 'Snap Ring Pliers' },
                    { zh: '螺丝刀', en: 'Screwdriver Set' },
                    { zh: '球头取出器', en: 'Ball Joint Separator' },
                    { zh: '油封钩', en: 'Seal Puller Hook' },
                ],
            },
            {
                id: 'garage-equipment',
                label: '举升设备',
                en: 'Garage Equipment',
                aliases: ['汽保设备', '维修设备', '车间设备', '汽修设备'],
                seeds: [
                    { zh: '千斤顶', en: 'Hydraulic Jack' },
                    { zh: '举升机', en: 'Two Post Lift' },
                    { zh: '支架', en: 'Jack Stand' },
                    { zh: '卧式千斤顶', en: 'Floor Jack' },
                    { zh: '吊机', en: 'Engine Crane' },
                    { zh: '变速箱托架', en: 'Transmission Jack' },
                    { zh: '轮胎拆装机', en: 'Tire Changer' },
                    { zh: '平衡机', en: 'Wheel Balancer' },
                ],
            },
            {
                id: 'paint-body',
                label: '钣金喷漆',
                en: 'Body Repair & Painting',
                aliases: ['钣金工具', '喷漆设备', '车身修复', '烤漆房'],
                seeds: [
                    { zh: '钣金锤', en: 'Body Hammer' },
                    { zh: '介子机', en: 'Stud Welder' },
                    { zh: '喷枪', en: 'Spray Gun' },
                    { zh: '打磨机', en: 'Air Sander' },
                    { zh: '烤漆灯', en: 'Infrared Curing Lamp' },
                    { zh: '凹陷拉拔器', en: 'Dent Puller' },
                    { zh: '遮蔽膜', en: 'Masking Film' },
                    { zh: '原子灰刮刀', en: 'Body Filler Spatula' },
                ],
            },
            {
                id: 'air-tools',
                label: '气动工具',
                en: 'Air Tools',
                aliases: ['风动工具', '气动设备', '风炮'],
                seeds: [
                    { zh: '气动扳手', en: 'Air Impact Wrench' },
                    { zh: '气动打磨机', en: 'Air Die Grinder' },
                    { zh: '气动铆钉枪', en: 'Air Riveter' },
                    { zh: '气动黄油枪', en: 'Air Grease Gun' },
                    { zh: '气动吹尘枪', en: 'Air Blow Gun' },
                    { zh: '气动砂轮机', en: 'Air Angle Grinder' },
                    { zh: '气动喷枪', en: 'Air Spray Gun' },
                    { zh: '气动棘轮扳手', en: 'Air Ratchet Wrench' },
                ],
            },
            {
                id: 'lubrication-care',
                label: '润滑养护',
                en: 'Lubrication & Care',
                aliases: ['保养工具', '换油设备', '养护用品', '润滑设备'],
                seeds: [
                    { zh: '黄油枪', en: 'Grease Gun' },
                    { zh: '抽油机', en: 'Oil Extractor' },
                    { zh: '加油漏斗', en: 'Oil Funnel' },
                    { zh: '废油盆', en: 'Oil Drain Pan' },
                    { zh: '注油器', en: 'Oil Injector' },
                    { zh: '冷却液更换机', en: 'Coolant Exchanger' },
                    { zh: '刹车油更换机', en: 'Brake Fluid Bleeder' },
                    { zh: '喷油嘴清洗机', en: 'Fuel Injector Cleaner' },
                ],
            },
        ],
    },
    {
        id: 'vehicle-parts',
        label: '汽车配件',
        en: 'Vehicle Parts & Accessories',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.steel.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'engine-system',
                label: '发动机件',
                en: 'Engine Parts',
                aliases: ['发动机配件', '引擎件', '动力件', '机头件'],
                seeds: [
                    { zh: '活塞', en: 'Engine Piston' },
                    { zh: '气缸套', en: 'Cylinder Liner' },
                    { zh: '曲轴', en: 'Crankshaft' },
                    { zh: '凸轮轴', en: 'Camshaft' },
                    { zh: '气门', en: 'Engine Valve' },
                    { zh: '连杆', en: 'Connecting Rod' },
                    { zh: '机油泵', en: 'Oil Pump' },
                    { zh: '水泵', en: 'Water Pump' },
                ],
            },
            {
                id: 'brake-system',
                label: '刹车件',
                en: 'Brake System Parts',
                aliases: ['制动件', '刹车配件', '制动系统', '刹车片'],
                seeds: [
                    { zh: '刹车片', en: 'Brake Pad' },
                    { zh: '刹车盘', en: 'Brake Disc' },
                    { zh: '刹车鼓', en: 'Brake Drum' },
                    { zh: '刹车总泵', en: 'Brake Master Cylinder' },
                    { zh: '刹车分泵', en: 'Brake Wheel Cylinder' },
                    { zh: '制动卡钳', en: 'Brake Caliper' },
                    { zh: '刹车软管', en: 'Brake Hose' },
                    { zh: '真空助力器', en: 'Brake Booster' },
                ],
            },
            {
                id: 'suspension-steering',
                label: '底盘件',
                en: 'Suspension & Steering',
                aliases: ['悬挂件', '转向件', '底盘配件', '减震件'],
                seeds: [
                    { zh: '减震器', en: 'Shock Absorber' },
                    { zh: '控制臂', en: 'Control Arm' },
                    { zh: '球头', en: 'Ball Joint' },
                    { zh: '拉杆', en: 'Tie Rod End' },
                    { zh: '稳定杆', en: 'Stabilizer Bar' },
                    { zh: '转向机', en: 'Steering Rack' },
                    { zh: '轮毂轴承', en: 'Wheel Hub Bearing' },
                    { zh: '衬套', en: 'Suspension Bushing' },
                ],
            },
            {
                id: 'electrical-lighting',
                label: '电器件',
                en: 'Auto Electrical Parts',
                aliases: ['汽车电器', '车灯件', '电路件', '电器配件'],
                seeds: [
                    { zh: '起动机', en: 'Starter Motor' },
                    { zh: '发电机', en: 'Alternator' },
                    { zh: '点火线圈', en: 'Ignition Coil' },
                    { zh: '火花塞', en: 'Spark Plug' },
                    { zh: '前大灯', en: 'Headlight Assembly' },
                    { zh: '尾灯', en: 'Tail Light' },
                    { zh: '雨刮电机', en: 'Wiper Motor' },
                    { zh: '氧传感器', en: 'Oxygen Sensor' },
                ],
            },
            {
                id: 'body-accessories',
                label: '车身附件',
                en: 'Body & Accessories',
                aliases: ['车身件', '外观件', '汽车附件', '装饰件'],
                seeds: [
                    { zh: '后视镜', en: 'Side Mirror' },
                    { zh: '保险杠', en: 'Bumper' },
                    { zh: '中网', en: 'Grille' },
                    { zh: '挡泥板', en: 'Mudguard' },
                    { zh: '车门把手', en: 'Door Handle' },
                    { zh: '行李架', en: 'Roof Rack' },
                    { zh: '脚踏板', en: 'Running Board' },
                    { zh: '雨刮片', en: 'Wiper Blade' },
                ],
            },
            {
                id: 'cooling-filter',
                label: '冷却滤清',
                en: 'Cooling & Filtration',
                aliases: ['散热件', '滤清器', '冷却系统', '三滤'],
                seeds: [
                    { zh: '散热器', en: 'Radiator' },
                    { zh: '冷凝器', en: 'Condenser' },
                    { zh: '机油滤清器', en: 'Oil Filter' },
                    { zh: '空气滤清器', en: 'Air Filter' },
                    { zh: '燃油滤清器', en: 'Fuel Filter' },
                    { zh: '空调滤芯', en: 'Cabin Air Filter' },
                    { zh: '电子风扇', en: 'Cooling Fan' },
                    { zh: '节温器', en: 'Thermostat' },
                ],
            },
        ],
    },
    {
        id: 'tools-hardware',
        label: '五金工具',
        en: 'Tools & Hardware',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.sienna.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'hand-tools',
                label: '手工具',
                en: 'Hand Tools',
                aliases: ['手动工具', '五金手工具', '手工工具', '通用手工具'],
                seeds: [
                    { zh: '羊角锤', en: 'Claw Hammer' },
                    { zh: '活动扳手', en: 'Adjustable Wrench' },
                    { zh: '钢丝钳', en: 'Combination Pliers' },
                    { zh: '螺丝刀', en: 'Screwdriver' },
                    { zh: '卷尺', en: 'Tape Measure' },
                    { zh: '美工刀', en: 'Utility Knife' },
                    { zh: '手锯', en: 'Hand Saw' },
                    { zh: '内六角扳手', en: 'Hex Key Set' },
                ],
            },
            {
                id: 'power-tools',
                label: '电动工具',
                en: 'Power Tools',
                aliases: ['电工具', '电动五金', '机电工具', '电动器械'],
                seeds: [
                    { zh: '电钻', en: 'Electric Drill' },
                    { zh: '角磨机', en: 'Angle Grinder' },
                    { zh: '电锤', en: 'Rotary Hammer' },
                    { zh: '电圆锯', en: 'Circular Saw' },
                    { zh: '曲线锯', en: 'Jigsaw' },
                    { zh: '电动扳手', en: 'Impact Wrench' },
                    { zh: '电刨', en: 'Electric Planer' },
                    { zh: '热风枪', en: 'Heat Gun' },
                ],
            },
            {
                id: 'fasteners',
                label: '紧固件',
                en: 'Fasteners',
                aliases: ['标准件', '螺丝螺母', '紧固五金', '连接件'],
                seeds: [
                    { zh: '自攻螺丝', en: 'Self-Tapping Screw' },
                    { zh: '六角螺栓', en: 'Hex Bolt' },
                    { zh: '膨胀螺栓', en: 'Expansion Anchor Bolt' },
                    { zh: '平垫圈', en: 'Flat Washer' },
                    { zh: '尼龙锁紧螺母', en: 'Nylon Lock Nut' },
                    { zh: '抽芯铆钉', en: 'Blind Rivet' },
                    { zh: '弹簧垫圈', en: 'Spring Washer' },
                    { zh: '木螺丝', en: 'Wood Screw' },
                ],
            },
            {
                id: 'locks-hardware',
                label: '锁具五金',
                en: 'Locks & Door Hardware',
                aliases: ['门锁五金', '建筑五金', '门窗五金', '锁具'],
                seeds: [
                    { zh: '挂锁', en: 'Padlock' },
                    { zh: '球形门锁', en: 'Knob Lock' },
                    { zh: '执手锁', en: 'Lever Handle Lock' },
                    { zh: '合页', en: 'Door Hinge' },
                    { zh: '门吸', en: 'Door Stopper' },
                    { zh: '闭门器', en: 'Door Closer' },
                    { zh: '插销', en: 'Door Bolt' },
                    { zh: '猫眼', en: 'Door Viewer' },
                ],
            },
            {
                id: 'abrasives-cutting',
                label: '磨料磨具',
                en: 'Abrasives & Cutting',
                aliases: ['磨具磨料', '切割片', '砂轮磨片', '打磨耗材'],
                seeds: [
                    { zh: '切割片', en: 'Cutting Disc' },
                    { zh: '角磨片', en: 'Grinding Disc' },
                    { zh: '砂纸', en: 'Sandpaper' },
                    { zh: '百叶轮', en: 'Flap Disc' },
                    { zh: '钢丝刷', en: 'Wire Brush' },
                    { zh: '金刚石锯片', en: 'Diamond Saw Blade' },
                    { zh: '开孔器', en: 'Hole Saw' },
                    { zh: '抛光轮', en: 'Polishing Wheel' },
                ],
            },
            {
                id: 'storage-measuring',
                label: '量具箱柜',
                en: 'Measuring & Storage',
                aliases: ['测量工具', '工具箱柜', '量具', '收纳五金'],
                seeds: [
                    { zh: '游标卡尺', en: 'Vernier Caliper' },
                    { zh: '水平尺', en: 'Spirit Level' },
                    { zh: '激光测距仪', en: 'Laser Distance Meter' },
                    { zh: '工具箱', en: 'Tool Box' },
                    { zh: '工具车', en: 'Tool Cabinet' },
                    { zh: '零件盒', en: 'Parts Organizer' },
                    { zh: '角尺', en: 'Try Square' },
                    { zh: '千分尺', en: 'Micrometer' },
                ],
            },
        ],
    },
    {
        id: 'renewable-energy',
        label: '新能源',
        en: 'Renewable Energy',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.teal.hex,
        tone: 'tech',
        segments: [
            {
                id: 'solar-modules',
                label: '光伏组件',
                en: 'Solar Modules',
                aliases: ['太阳能板', '光伏板', '电池板'],
                seeds: [
                    { zh: '单晶硅组件', en: 'Monocrystalline Solar Panel' },
                    { zh: '多晶硅组件', en: 'Polycrystalline Solar Panel' },
                    { zh: '双面双玻组件', en: 'Bifacial Dual Glass Module' },
                    { zh: '柔性太阳能板', en: 'Flexible Solar Panel' },
                    { zh: '半片电池组件', en: 'Half-Cell Solar Module' },
                    { zh: '叠瓦组件', en: 'Shingled Solar Module' },
                    { zh: '太阳能瓦', en: 'Solar Roof Tile' },
                    { zh: '便携太阳能板', en: 'Portable Solar Panel' },
                ],
            },
            {
                id: 'energy-storage',
                label: '储能电池',
                en: 'Energy Storage Systems',
                aliases: ['储能系统', '电池储能', '储能柜', '锂电池组'],
                seeds: [
                    { zh: '磷酸铁锂电池', en: 'LiFePO4 Battery Pack' },
                    { zh: '家庭储能电源', en: 'Home Battery Backup' },
                    { zh: '工商业储能柜', en: 'Commercial Battery Cabinet' },
                    { zh: '便携储能电源', en: 'Portable Power Station' },
                    { zh: '储能逆变器', en: 'Hybrid Storage Inverter' },
                    { zh: '电池管理系统', en: 'Battery Management System' },
                    { zh: '铅碳电池', en: 'Lead-Carbon Battery' },
                    { zh: '集装箱储能', en: 'Containerized Storage Unit' },
                ],
            },
            {
                id: 'wind-power',
                label: '风电设备',
                en: 'Wind Power Equipment',
                aliases: ['风力发电', '风机', '风电机组', '风电配件'],
                seeds: [
                    { zh: '风力发电机', en: 'Wind Turbine Generator' },
                    { zh: '风机叶片', en: 'Wind Turbine Blade' },
                    { zh: '垂直轴风机', en: 'Vertical Axis Wind Turbine' },
                    { zh: '风机塔筒', en: 'Wind Turbine Tower' },
                    { zh: '风机齿轮箱', en: 'Wind Turbine Gearbox' },
                    { zh: '偏航系统', en: 'Yaw System' },
                    { zh: '小型风力发电机', en: 'Small Wind Turbine' },
                    { zh: '风机法兰', en: 'Wind Turbine Flange' },
                ],
            },
            {
                id: 'inverters',
                label: '逆变电源',
                en: 'Inverters and Power Electronics',
                aliases: ['逆变器', '光伏逆变器', '电源转换器', '变频电源'],
                seeds: [
                    { zh: '并网逆变器', en: 'Grid-Tie Inverter' },
                    { zh: '离网逆变器', en: 'Off-Grid Inverter' },
                    { zh: '微型逆变器', en: 'Microinverter' },
                    { zh: '正弦波逆变器', en: 'Pure Sine Wave Inverter' },
                    { zh: 'MPPT控制器', en: 'MPPT Charge Controller' },
                    { zh: '光伏优化器', en: 'Solar Power Optimizer' },
                    { zh: '变频器', en: 'Variable Frequency Drive' },
                    { zh: '整流电源', en: 'Rectifier Power Supply' },
                ],
            },
            {
                id: 'solar-mounting',
                label: '光伏支架',
                en: 'Solar Mounting Structures',
                aliases: ['支架系统', '安装支架', '跟踪支架'],
                seeds: [
                    { zh: '地面支架', en: 'Ground Mount Rack' },
                    { zh: '屋顶支架', en: 'Roof Mounting Bracket' },
                    { zh: '跟踪支架', en: 'Solar Tracker' },
                    { zh: '铝合金导轨', en: 'Aluminum Mounting Rail' },
                    { zh: '光伏夹具', en: 'Solar Panel Clamp' },
                    { zh: '车棚支架', en: 'Solar Carport Structure' },
                    { zh: '立柱支架', en: 'Pole Mount Bracket' },
                    { zh: '漂浮支架', en: 'Floating Solar Mount' },
                ],
            },
            {
                id: 'solar-thermal',
                label: '光热产品',
                en: 'Solar Thermal Products',
                aliases: ['太阳能热水器', '光热利用', '太阳能集热', '热水工程'],
                seeds: [
                    { zh: '太阳能热水器', en: 'Solar Water Heater' },
                    { zh: '真空集热管', en: 'Vacuum Tube Collector' },
                    { zh: '平板集热器', en: 'Flat Plate Collector' },
                    { zh: '太阳能开水器', en: 'Solar Water Boiler' },
                    { zh: '空气源热泵', en: 'Air Source Heat Pump' },
                    { zh: '太阳能烘干机', en: 'Solar Dryer' },
                    { zh: '太阳能灶', en: 'Solar Cooker' },
                    { zh: '储热水箱', en: 'Hot Water Storage Tank' },
                ],
            },
        ],
    },
    {
        id: 'electrical-equipment',
        label: '电气设备',
        en: 'Electrical Equipment & Supplies',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.steel.hex,
        tone: 'tech',
        segments: [
            {
                id: 'switchgear-breakers',
                label: '开关电器',
                en: 'Switchgear & Breakers',
                aliases: ['低压电器', '断路器', '配电开关', '开关柜'],
                seeds: [
                    { zh: '塑壳断路器', en: 'Molded Case Circuit Breaker' },
                    { zh: '微型断路器', en: 'Miniature Circuit Breaker' },
                    { zh: '交流接触器', en: 'AC Contactor' },
                    { zh: '热继电器', en: 'Thermal Overload Relay' },
                    { zh: '隔离开关', en: 'Isolator Switch' },
                    { zh: '真空断路器', en: 'Vacuum Circuit Breaker' },
                    { zh: '万能转换开关', en: 'Universal Changeover Switch' },
                    { zh: '浪涌保护器', en: 'Surge Protective Device' },
                ],
            },
            {
                id: 'transformers',
                label: '变压器',
                en: 'Transformers',
                aliases: ['电力变压器', '干式变压器', '油浸变压器', '互感器'],
                seeds: [
                    { zh: '干式变压器', en: 'Dry Type Transformer' },
                    { zh: '油浸变压器', en: 'Oil Immersed Transformer' },
                    { zh: '箱式变电站', en: 'Prefabricated Substation' },
                    { zh: '电流互感器', en: 'Current Transformer' },
                    { zh: '电压互感器', en: 'Voltage Transformer' },
                    { zh: '稳压器', en: 'Voltage Stabilizer' },
                    { zh: '隔离变压器', en: 'Isolation Transformer' },
                    { zh: '电抗器', en: 'Reactor' },
                ],
            },
            {
                id: 'wires-cables',
                label: '电线电缆',
                en: 'Wires & Cables',
                aliases: ['电缆', '线缆', '电力电缆', '布电线'],
                seeds: [
                    { zh: '电力电缆', en: 'Power Cable' },
                    { zh: '控制电缆', en: 'Control Cable' },
                    { zh: '布电线', en: 'Building Wire' },
                    { zh: '橡套电缆', en: 'Rubber Sheathed Cable' },
                    { zh: '架空绝缘导线', en: 'Aerial Insulated Conductor' },
                    { zh: '同轴电缆', en: 'Coaxial Cable' },
                    { zh: '网络线', en: 'Network Cable' },
                    { zh: '电焊机电缆', en: 'Welding Cable' },
                ],
            },
            {
                id: 'motors-drives',
                label: '电机驱动',
                en: 'Motors & Drives',
                aliases: ['电动机', '电机', '变频器', '减速电机'],
                seeds: [
                    { zh: '三相异步电动机', en: 'Three Phase Induction Motor' },
                    { zh: '单相电机', en: 'Single Phase Motor' },
                    { zh: '变频器', en: 'Variable Frequency Drive' },
                    { zh: '伺服电机', en: 'Servo Motor' },
                    { zh: '减速电机', en: 'Gear Motor' },
                    { zh: '软启动器', en: 'Soft Starter' },
                    { zh: '直流电机', en: 'DC Motor' },
                    { zh: '水泵电机', en: 'Water Pump Motor' },
                ],
            },
            {
                id: 'wiring-accessories',
                label: '接线附件',
                en: 'Wiring Accessories',
                aliases: ['接线端子', '电气辅件', '开关插座', '接线盒'],
                seeds: [
                    { zh: '接线端子', en: 'Terminal Block' },
                    { zh: '冷压端子', en: 'Crimp Terminal' },
                    { zh: '墙壁开关', en: 'Wall Switch' },
                    { zh: '电源插座', en: 'Power Socket' },
                    { zh: '接线盒', en: 'Junction Box' },
                    { zh: '电缆接头', en: 'Cable Gland' },
                    { zh: '热缩管', en: 'Heat Shrink Tubing' },
                    { zh: '线槽', en: 'Cable Trunking' },
                ],
            },
            {
                id: 'distribution-boards',
                label: '配电柜箱',
                en: 'Distribution Boards',
                aliases: ['配电箱', '配电柜', '控制柜', '电控箱'],
                seeds: [
                    { zh: '配电箱', en: 'Distribution Box' },
                    { zh: '动力配电柜', en: 'Power Distribution Cabinet' },
                    { zh: '照明配电箱', en: 'Lighting Distribution Board' },
                    { zh: '控制柜', en: 'Control Cabinet' },
                    { zh: '电表箱', en: 'Meter Box' },
                    { zh: '电容补偿柜', en: 'Capacitor Compensation Cabinet' },
                    { zh: '母线槽', en: 'Busbar Trunking' },
                    { zh: '电缆桥架', en: 'Cable Tray' },
                ],
            },
        ],
    },
    {
        id: 'safety-security',
        label: '安防与劳保',
        en: 'Safety & Security',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.charcoal.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'video-surveillance',
                label: '视频监控',
                en: 'Video Surveillance',
                aliases: ['监控设备', '安防监控', '摄像系统', '监控摄像头'],
                seeds: [
                    { zh: '网络摄像机', en: 'Network IP Camera' },
                    { zh: '半球摄像机', en: 'Dome Camera' },
                    { zh: '球机', en: 'PTZ Speed Dome' },
                    { zh: '硬盘录像机', en: 'Network Video Recorder' },
                    { zh: '监控主机', en: 'Standalone DVR' },
                    { zh: '监控镜头', en: 'CCTV Lens' },
                    { zh: '红外补光灯', en: 'IR Illuminator' },
                    { zh: '监控电源', en: 'CCTV Power Supply' },
                ],
            },
            {
                id: 'access-control',
                label: '门禁对讲',
                en: 'Access Control',
                aliases: ['门禁系统', '出入口控制', '楼宇对讲', '刷卡门禁'],
                seeds: [
                    { zh: '门禁一体机', en: 'Standalone Access Controller' },
                    { zh: '读卡器', en: 'RFID Card Reader' },
                    { zh: '电磁锁', en: 'Electromagnetic Lock' },
                    { zh: '电插锁', en: 'Electric Bolt Lock' },
                    { zh: '出门按钮', en: 'Exit Push Button' },
                    { zh: '可视对讲', en: 'Video Door Phone' },
                    { zh: '指纹锁', en: 'Fingerprint Door Lock' },
                    { zh: '闸机', en: 'Turnstile Gate' },
                ],
            },
            {
                id: 'intrusion-alarm',
                label: '防盗报警',
                en: 'Intrusion Alarm',
                aliases: ['报警器', '防盗报警系统', '入侵探测', '安防报警'],
                seeds: [
                    { zh: '红外探测器', en: 'PIR Motion Sensor' },
                    { zh: '门磁', en: 'Magnetic Door Contact' },
                    { zh: '烟感探测器', en: 'Smoke Detector' },
                    { zh: '燃气报警器', en: 'Gas Leak Alarm' },
                    { zh: '声光警号', en: 'Siren Strobe' },
                    { zh: '报警主机', en: 'Alarm Control Panel' },
                    { zh: '玻璃破碎探测器', en: 'Glass Break Detector' },
                    { zh: '紧急按钮', en: 'Panic Button' },
                ],
            },
            {
                id: 'fire-safety',
                label: '消防器材',
                en: 'Fire Safety Equipment',
                aliases: ['消防设备', '灭火器材', '消防用品', '防火设备'],
                seeds: [
                    { zh: '灭火器', en: 'Fire Extinguisher' },
                    { zh: '消防水带', en: 'Fire Hose' },
                    { zh: '消防栓', en: 'Fire Hydrant' },
                    { zh: '消防应急灯', en: 'Emergency Exit Light' },
                    { zh: '消防服', en: 'Firefighter Suit' },
                    { zh: '灭火毯', en: 'Fire Blanket' },
                    { zh: '消防喷头', en: 'Fire Sprinkler Head' },
                    { zh: '防毒面具', en: 'Escape Respirator' },
                ],
            },
            {
                id: 'ppe',
                label: '劳保用品',
                en: 'Personal Protective Equipment',
                aliases: ['防护用品', '劳动保护', '安全防护', '个体防护'],
                seeds: [
                    { zh: '安全帽', en: 'Safety Helmet' },
                    { zh: '防护眼镜', en: 'Safety Goggles' },
                    { zh: '防尘口罩', en: 'Dust Mask' },
                    { zh: '劳保手套', en: 'Work Gloves' },
                    { zh: '安全鞋', en: 'Safety Shoes' },
                    { zh: '反光背心', en: 'Reflective Vest' },
                    { zh: '安全带', en: 'Fall Arrest Harness' },
                    { zh: '耳罩', en: 'Ear Muffs' },
                ],
            },
            {
                id: 'barrier-security',
                label: '安防围栏',
                en: 'Perimeter Security',
                aliases: ['防护围栏', '隔离设施', '周界防护'],
                seeds: [
                    { zh: '电子围栏', en: 'Electric Fence' },
                    { zh: '隔离护栏', en: 'Crowd Barrier' },
                    { zh: '防撞柱', en: 'Bollard' },
                    { zh: '减速带', en: 'Speed Bump' },
                    { zh: '反光锥', en: 'Traffic Cone' },
                    { zh: '警戒带', en: 'Warning Tape' },
                    { zh: '刺绳', en: 'Razor Wire' },
                    { zh: '防爆毯', en: 'Blast Blanket' },
                ],
            },
        ],
    },
    {
        id: 'material-handling',
        label: '物料搬运',
        en: 'Material Handling',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.sienna.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'pallet-trucks',
                label: '搬运车',
                en: 'Pallet Trucks',
                aliases: ['手动液压车', '地牛', '托盘车', '叉车替代'],
                seeds: [
                    { zh: '手动液压搬运车', en: 'Manual Hydraulic Pallet Truck' },
                    { zh: '电动托盘搬运车', en: 'Electric Pallet Truck' },
                    { zh: '电子秤搬运车', en: 'Pallet Truck With Scale' },
                    { zh: '剪叉式升高车', en: 'Scissor Lift Pallet Truck' },
                    { zh: '纸筒搬运车', en: 'Drum Pallet Truck' },
                    { zh: '折叠式搬运车', en: 'Folding Hand Pallet Truck' },
                    { zh: '不锈钢搬运车', en: 'Stainless Steel Pallet Truck' },
                    { zh: '称重地牛', en: 'Weighing Pallet Jack' },
                ],
            },
            {
                id: 'casters-wheels',
                label: '脚轮',
                en: 'Casters And Wheels',
                aliases: ['万向轮', '工业脚轮', '轮子', '脚轮配件'],
                seeds: [
                    { zh: '万向脚轮', en: 'Swivel Caster Wheel' },
                    { zh: '定向脚轮', en: 'Rigid Caster Wheel' },
                    { zh: '刹车脚轮', en: 'Brake Caster Wheel' },
                    { zh: '聚氨酯脚轮', en: 'Polyurethane Caster' },
                    { zh: '尼龙脚轮', en: 'Nylon Caster Wheel' },
                    { zh: '橡胶脚轮', en: 'Rubber Caster Wheel' },
                    { zh: '重型脚轮', en: 'Heavy Duty Caster' },
                    { zh: '医疗脚轮', en: 'Medical Caster Wheel' },
                ],
            },
            {
                id: 'conveyors',
                label: '输送机',
                en: 'Conveyors',
                aliases: ['输送线', '传送带', '流水线', '输送设备'],
                seeds: [
                    { zh: '皮带输送机', en: 'Belt Conveyor' },
                    { zh: '滚筒输送机', en: 'Roller Conveyor' },
                    { zh: '链板输送机', en: 'Chain Plate Conveyor' },
                    { zh: '螺旋输送机', en: 'Screw Conveyor' },
                    { zh: '伸缩输送机', en: 'Telescopic Belt Conveyor' },
                    { zh: '转弯输送机', en: 'Curved Belt Conveyor' },
                    { zh: '爬坡输送机', en: 'Inclined Belt Conveyor' },
                    { zh: '悬挂输送线', en: 'Overhead Conveyor Line' },
                ],
            },
            {
                id: 'lifting-equipment',
                label: '起重设备',
                en: 'Lifting Equipment',
                aliases: ['吊装设备', '起重工具', '葫芦', '吊具'],
                seeds: [
                    { zh: '手拉葫芦', en: 'Manual Chain Hoist' },
                    { zh: '电动葫芦', en: 'Electric Chain Hoist' },
                    { zh: '千斤顶', en: 'Hydraulic Jack' },
                    { zh: '吊装带', en: 'Webbing Sling' },
                    { zh: '钢丝绳吊索', en: 'Wire Rope Sling' },
                    { zh: '永磁起重器', en: 'Magnetic Lifter' },
                    { zh: '吊钩', en: 'Lifting Hook' },
                    { zh: '平衡吊', en: 'Balance Crane' },
                ],
            },
            {
                id: 'racking-storage',
                label: '仓储货架',
                en: 'Racking And Shelving',
                aliases: ['货架', '仓储架', '置物架', '库房货架'],
                seeds: [
                    { zh: '横梁货架', en: 'Selective Pallet Rack' },
                    { zh: '贯通式货架', en: 'Drive-In Pallet Rack' },
                    { zh: '阁楼货架', en: 'Mezzanine Rack' },
                    { zh: '悬臂货架', en: 'Cantilever Rack' },
                    { zh: '流利式货架', en: 'Carton Flow Rack' },
                    { zh: '轻型货架', en: 'Light Duty Shelving' },
                    { zh: '模具货架', en: 'Mould Storage Rack' },
                    { zh: '堆垛架', en: 'Stacking Rack' },
                ],
            },
            {
                id: 'containers-bins',
                label: '周转容器',
                en: 'Containers And Bins',
                aliases: ['周转箱', '塑料箱', '料箱', '周转筐'],
                seeds: [
                    { zh: '塑料周转箱', en: 'Plastic Turnover Box' },
                    { zh: '折叠周转箱', en: 'Collapsible Plastic Crate' },
                    { zh: '塑料托盘', en: 'Plastic Pallet' },
                    { zh: '木托盘', en: 'Wooden Pallet' },
                    { zh: '零件盒', en: 'Plastic Parts Bin' },
                    { zh: '周转筐', en: 'Plastic Turnover Basket' },
                    { zh: '金属料箱', en: 'Metal Storage Bin' },
                    { zh: '物流台车', en: 'Logistics Trolley Cart' },
                ],
            },
        ],
    },
    {
        id: 'testing-instrument',
        label: '检测仪器',
        en: 'Testing Instrument & Equipment',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.slate.hex,
        tone: 'tech',
        segments: [
            {
                id: 'material-testing',
                label: '材料检测',
                en: 'Material Testing Instruments',
                aliases: ['物性测试', '力学检测', '材料试验', '物理性能检测'],
                seeds: [
                    { zh: '万能试验机', en: 'Universal Testing Machine' },
                    { zh: '硬度计', en: 'Hardness Tester' },
                    { zh: '冲击试验机', en: 'Impact Testing Machine' },
                    { zh: '熔融指数仪', en: 'Melt Flow Indexer' },
                    { zh: '密度天平', en: 'Density Balance' },
                    { zh: '热变形维卡仪', en: 'HDT Vicat Tester' },
                    { zh: '摩擦磨损试验机', en: 'Friction Wear Tester' },
                    { zh: '盐雾试验箱', en: 'Salt Spray Test Chamber' },
                ],
            },
            {
                id: 'electrical-testing',
                label: '电气检测',
                en: 'Electrical Testing Instruments',
                aliases: ['电性能测试', '安规检测', '电子测量', '电参数测试'],
                seeds: [
                    { zh: '万用表', en: 'Digital Multimeter' },
                    { zh: '示波器', en: 'Digital Oscilloscope' },
                    { zh: '耐压测试仪', en: 'Hipot Tester' },
                    { zh: '绝缘电阻测试仪', en: 'Insulation Resistance Tester' },
                    { zh: '接地电阻测试仪', en: 'Earth Resistance Tester' },
                    { zh: '功率分析仪', en: 'Power Analyzer' },
                    { zh: 'LCR测试仪', en: 'LCR Meter' },
                    { zh: '电池内阻测试仪', en: 'Battery Internal Resistance Tester' },
                ],
            },
            {
                id: 'environmental-testing',
                label: '环境试验',
                en: 'Environmental Test Chambers',
                aliases: ['气候试验', '温湿度试验', '老化试验', '环境模拟'],
                seeds: [
                    { zh: '恒温恒湿箱', en: 'Constant Temperature Humidity Chamber' },
                    { zh: '高低温试验箱', en: 'High Low Temperature Chamber' },
                    { zh: '冷热冲击箱', en: 'Thermal Shock Chamber' },
                    { zh: '紫外老化箱', en: 'UV Aging Test Chamber' },
                    { zh: '氙灯老化箱', en: 'Xenon Arc Aging Chamber' },
                    { zh: '淋雨试验箱', en: 'Rain Test Chamber' },
                    { zh: '沙尘试验箱', en: 'Dust Test Chamber' },
                    { zh: '真空干燥箱', en: 'Vacuum Drying Oven' },
                ],
            },
            {
                id: 'optical-measurement',
                label: '光学测量',
                en: 'Optical Measurement Instruments',
                aliases: ['尺寸测量', '影像测量', '光学检测', '几何量测'],
                seeds: [
                    { zh: '影像测量仪', en: 'Vision Measuring Machine' },
                    { zh: '投影仪', en: 'Profile Projector' },
                    { zh: '工具显微镜', en: 'Toolmakers Microscope' },
                    { zh: '激光测距仪', en: 'Laser Distance Meter' },
                    { zh: '粗糙度仪', en: 'Surface Roughness Tester' },
                    { zh: '圆度仪', en: 'Roundness Measuring Instrument' },
                    { zh: '色差仪', en: 'Colorimeter' },
                    { zh: '光泽度仪', en: 'Gloss Meter' },
                ],
            },
            {
                id: 'analysis-instruments',
                label: '分析仪器',
                en: 'Analytical Instruments',
                aliases: ['成分分析', '实验室分析', '理化分析', '检测分析'],
                seeds: [
                    { zh: '分光光度计', en: 'Spectrophotometer' },
                    { zh: '气相色谱仪', en: 'Gas Chromatograph' },
                    { zh: '液相色谱仪', en: 'Liquid Chromatograph' },
                    { zh: '电子天平', en: 'Electronic Analytical Balance' },
                    { zh: '酸度计', en: 'pH Meter' },
                    { zh: '电导率仪', en: 'Conductivity Meter' },
                    { zh: '水分测定仪', en: 'Moisture Analyzer' },
                    { zh: '马弗炉', en: 'Muffle Furnace' },
                ],
            },
            {
                id: 'nondestructive-testing',
                label: '无损检测',
                en: 'Nondestructive Testing Equipment',
                aliases: ['探伤检测', '无损探伤', '超声检测', '缺陷检测'],
                seeds: [
                    { zh: '超声波探伤仪', en: 'Ultrasonic Flaw Detector' },
                    { zh: '涂层测厚仪', en: 'Coating Thickness Gauge' },
                    { zh: '超声波测厚仪', en: 'Ultrasonic Thickness Gauge' },
                    { zh: '磁粉探伤机', en: 'Magnetic Particle Inspection Machine' },
                    { zh: '涡流探伤仪', en: 'Eddy Current Flaw Detector' },
                    { zh: 'X射线探伤机', en: 'X-Ray Inspection Machine' },
                    { zh: '里氏硬度计', en: 'Leeb Hardness Tester' },
                    { zh: '内窥镜', en: 'Industrial Borescope' },
                ],
            },
        ],
    },
    {
        id: 'power-transmission',
        label: '传动件',
        en: 'Power Transmission',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.steel.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'gear-drive',
                label: '齿轮传动',
                en: 'Gears & Gear Drives',
                aliases: ['齿轮', '传动齿轮', '齿轮箱', '减速齿轮'],
                seeds: [
                    { zh: '直齿轮', en: 'Spur Gear' },
                    { zh: '斜齿轮', en: 'Helical Gear' },
                    { zh: '锥齿轮', en: 'Bevel Gear' },
                    { zh: '蜗轮蜗杆', en: 'Worm Gear Set' },
                    { zh: '齿轮减速机', en: 'Gear Reducer' },
                    { zh: '行星减速机', en: 'Planetary Gearbox' },
                    { zh: '齿轮齿条', en: 'Gear Rack' },
                    { zh: '同步带轮', en: 'Timing Pulley' },
                ],
            },
            {
                id: 'belt-drive',
                label: '皮带传动',
                en: 'Belts & Pulleys',
                aliases: ['传动带', '皮带轮', '同步带', '三角带'],
                seeds: [
                    { zh: '三角带', en: 'V-Belt' },
                    { zh: '同步带', en: 'Timing Belt' },
                    { zh: '平皮带', en: 'Flat Belt' },
                    { zh: '多楔带', en: 'Poly-V Belt' },
                    { zh: '锥套皮带轮', en: 'Taper Lock Pulley' },
                    { zh: '同步带轮', en: 'Timing Belt Pulley' },
                    { zh: '涨紧轮', en: 'Idler Pulley' },
                    { zh: '皮带扣', en: 'Belt Fastener' },
                ],
            },
            {
                id: 'chain-drive',
                label: '链条传动',
                en: 'Chains & Sprockets',
                aliases: ['传动链', '链轮', '滚子链', '链条'],
                seeds: [
                    { zh: '滚子链', en: 'Roller Chain' },
                    { zh: '双节距链条', en: 'Double Pitch Chain' },
                    { zh: '链轮', en: 'Chain Sprocket' },
                    { zh: '板式链', en: 'Leaf Chain' },
                    { zh: '齿形链', en: 'Silent Chain' },
                    { zh: '输送链', en: 'Conveyor Chain' },
                    { zh: '链条接头', en: 'Chain Connecting Link' },
                    { zh: '涨紧链轮', en: 'Chain Tensioner Sprocket' },
                ],
            },
            {
                id: 'coupling-shaft',
                label: '联轴器轴',
                en: 'Couplings & Shafts',
                aliases: ['联轴器', '传动轴', '轴套', '万向节'],
                seeds: [
                    { zh: '梅花联轴器', en: 'Jaw Coupling' },
                    { zh: '膜片联轴器', en: 'Disc Coupling' },
                    { zh: '弹性柱销联轴器', en: 'Pin Coupling' },
                    { zh: '万向节', en: 'Universal Joint' },
                    { zh: '传动轴', en: 'Drive Shaft' },
                    { zh: '花键轴', en: 'Spline Shaft' },
                    { zh: '直线光轴', en: 'Linear Shaft' },
                    { zh: '轴套', en: 'Shaft Sleeve' },
                ],
            },
            {
                id: 'bearing-transmission',
                label: '轴承',
                en: 'Bearings',
                aliases: ['滚动轴承', '轴承座', '关节轴承', '直线轴承'],
                seeds: [
                    { zh: '深沟球轴承', en: 'Deep Groove Ball Bearing' },
                    { zh: '圆锥滚子轴承', en: 'Tapered Roller Bearing' },
                    { zh: '调心球轴承', en: 'Self-Aligning Ball Bearing' },
                    { zh: '推力球轴承', en: 'Thrust Ball Bearing' },
                    { zh: '直线轴承', en: 'Linear Bearing' },
                    { zh: '带座轴承', en: 'Pillow Block Bearing' },
                    { zh: '关节轴承', en: 'Spherical Plain Bearing' },
                    { zh: '滚针轴承', en: 'Needle Roller Bearing' },
                ],
            },
            {
                id: 'clutch-brake',
                label: '离合制动',
                en: 'Clutches & Brakes',
                aliases: ['离合器', '制动器', '电磁离合器', '刹车器'],
                seeds: [
                    { zh: '电磁离合器', en: 'Electromagnetic Clutch' },
                    { zh: '电磁制动器', en: 'Electromagnetic Brake' },
                    { zh: '气动离合器', en: 'Pneumatic Clutch' },
                    { zh: '单向离合器', en: 'One-Way Clutch' },
                    { zh: '摩擦片', en: 'Friction Disc' },
                    { zh: '磁粉离合器', en: 'Magnetic Particle Clutch' },
                    { zh: '安全离合器', en: 'Torque Limiter Clutch' },
                    { zh: '气动制动器', en: 'Pneumatic Brake' },
                ],
            },
        ],
    },
    {
        id: 'electronic-components',
        label: '电子元器件',
        en: 'Electronic Components',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.slate.hex,
        tone: 'tech',
        segments: [
            {
                id: 'passive-components',
                label: '被动元件',
                en: 'Passive Components',
                aliases: ['无源器件', '被动器件', '阻容元件', '基础元件'],
                seeds: [
                    { zh: '贴片电阻', en: 'SMD Chip Resistor' },
                    { zh: '陶瓷电容', en: 'Ceramic Capacitor' },
                    { zh: '铝电解电容', en: 'Aluminum Electrolytic Capacitor' },
                    { zh: '功率电感', en: 'Power Inductor' },
                    { zh: '共模电感', en: 'Common Mode Choke' },
                    { zh: '压敏电阻', en: 'Varistor' },
                    { zh: '热敏电阻', en: 'Thermistor' },
                    { zh: '晶振', en: 'Crystal Oscillator' },
                ],
            },
            {
                id: 'discrete-semiconductors',
                label: '分立器件',
                en: 'Discrete Semiconductors',
                aliases: ['半导体分立器件', '二三极管', '功率器件', '分立元件'],
                seeds: [
                    { zh: '整流二极管', en: 'Rectifier Diode' },
                    { zh: '肖特基二极管', en: 'Schottky Diode' },
                    { zh: '稳压二极管', en: 'Zener Diode' },
                    { zh: '三极管', en: 'Bipolar Transistor' },
                    { zh: 'MOS管', en: 'MOSFET' },
                    { zh: '可控硅', en: 'Thyristor' },
                    { zh: '桥式整流器', en: 'Bridge Rectifier' },
                    { zh: '开关二极管', en: 'Switching Diode' },
                ],
            },
            {
                id: 'integrated-circuits',
                label: '集成电路',
                en: 'Integrated Circuits',
                aliases: ['芯片', 'IC', '集成块', '半导体芯片'],
                seeds: [
                    { zh: '运算放大器', en: 'Operational Amplifier' },
                    { zh: '电源管理芯片', en: 'Power Management IC' },
                    { zh: '单片机', en: 'Microcontroller' },
                    { zh: '逻辑芯片', en: 'Logic Gate IC' },
                    { zh: '存储芯片', en: 'Memory IC' },
                    { zh: '驱动芯片', en: 'Driver IC' },
                    { zh: '接口芯片', en: 'Interface IC' },
                    { zh: '电压基准芯片', en: 'Voltage Reference IC' },
                ],
            },
            {
                id: 'connectors',
                label: '连接器',
                en: 'Connectors',
                aliases: ['接插件', '插接件', '端子连接器', '连接器件'],
                seeds: [
                    { zh: '排针排母', en: 'Pin Header and Socket' },
                    { zh: '牛角插座', en: 'Box Header Connector' },
                    { zh: 'FPC连接器', en: 'FPC Connector' },
                    { zh: '圆针连接器', en: 'Circular Connector' },
                    { zh: '接线端子', en: 'Terminal Block' },
                    { zh: '板对板连接器', en: 'Board-to-Board Connector' },
                    { zh: '航空插头', en: 'Aviation Plug Connector' },
                    { zh: 'DC插座', en: 'DC Power Jack' },
                ],
            },
            {
                id: 'pcb-assembly',
                label: '线路板',
                en: 'PCB and Assembly',
                aliases: ['印制板', '电路板', 'PCBA', '线路板组件'],
                seeds: [
                    { zh: '单面板', en: 'Single-Sided PCB' },
                    { zh: '双面板', en: 'Double-Sided PCB' },
                    { zh: '多层板', en: 'Multilayer PCB' },
                    { zh: '铝基板', en: 'Aluminum PCB' },
                    { zh: '柔性线路板', en: 'Flexible PCB' },
                    { zh: '高频板', en: 'High Frequency PCB' },
                    { zh: '贴片加工板', en: 'SMT Assembly Board' },
                    { zh: '插件焊接板', en: 'Through-Hole Assembly Board' },
                ],
            },
            {
                id: 'electromechanical',
                label: '机电元件',
                en: 'Electromechanical Components',
                aliases: ['机电件', '开关继电器', '机电元器件', '控制元件'],
                seeds: [
                    { zh: '轻触开关', en: 'Tactile Switch' },
                    { zh: '拨动开关', en: 'Toggle Switch' },
                    { zh: '继电器', en: 'Electromagnetic Relay' },
                    { zh: '固态继电器', en: 'Solid State Relay' },
                    { zh: '蜂鸣器', en: 'Buzzer' },
                    { zh: '保险丝', en: 'Fuse' },
                    { zh: '电位器', en: 'Potentiometer' },
                    { zh: '旋转编码器', en: 'Rotary Encoder' },
                ],
            },
        ],
    },
    {
        id: 'vehicles-transport',
        label: '整车与交通',
        en: 'Vehicles & Transportation',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.steel.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'auto-vehicles',
                label: '整车',
                en: 'Complete Vehicles',
                aliases: ['汽车整车', '乘用车', '商用车', '整车出口'],
                seeds: [
                    { zh: '轿车', en: 'Passenger Car' },
                    { zh: 'SUV', en: 'Sport Utility Vehicle' },
                    { zh: '皮卡', en: 'Pickup Truck' },
                    { zh: '轻卡', en: 'Light Truck' },
                    { zh: '客车', en: 'Passenger Bus' },
                    { zh: '牵引车', en: 'Tractor Truck' },
                    { zh: '自卸车', en: 'Dump Truck' },
                    { zh: '微型面包车', en: 'Mini Van' },
                ],
            },
            {
                id: 'ev-new-energy',
                label: '新能源车',
                en: 'Electric Vehicles',
                aliases: ['电动车', '新能源汽车', '纯电车', '电动商用车'],
                seeds: [
                    { zh: '纯电轿车', en: 'Electric Sedan' },
                    { zh: '电动SUV', en: 'Electric SUV' },
                    { zh: '电动轻卡', en: 'Electric Light Truck' },
                    { zh: '电动客车', en: 'Electric Bus' },
                    { zh: '电动环卫车', en: 'Electric Sanitation Truck' },
                    { zh: '电动叉车', en: 'Electric Forklift' },
                    { zh: '电动观光车', en: 'Electric Sightseeing Cart' },
                    { zh: '电动三轮车', en: 'Electric Tricycle' },
                ],
            },
            {
                id: 'trailers-bodies',
                label: '挂车车厢',
                en: 'Trailers & Bodies',
                aliases: ['半挂车', '专用车', '车厢', '挂车'],
                seeds: [
                    { zh: '平板半挂车', en: 'Flatbed Semi Trailer' },
                    { zh: '仓栅半挂车', en: 'Stake Semi Trailer' },
                    { zh: '厢式半挂车', en: 'Box Semi Trailer' },
                    { zh: '罐式半挂车', en: 'Tank Semi Trailer' },
                    { zh: '冷藏车厢', en: 'Refrigerated Truck Body' },
                    { zh: '自卸车厢', en: 'Dump Truck Body' },
                    { zh: '旅居房车', en: 'Recreational Vehicle' },
                    { zh: '清障车', en: 'Wrecker Truck' },
                ],
            },
            {
                id: 'parts-components',
                label: '汽车配件',
                en: 'Auto Parts',
                aliases: ['零部件', '汽配', '配件', '易损件'],
                seeds: [
                    { zh: '刹车片', en: 'Brake Pad' },
                    { zh: '减震器', en: 'Shock Absorber' },
                    { zh: '离合器', en: 'Clutch Assembly' },
                    { zh: '车灯', en: 'Vehicle Lamp' },
                    { zh: '后视镜', en: 'Side Mirror' },
                    { zh: '轮毂', en: 'Alloy Wheel' },
                    { zh: '散热器', en: 'Radiator' },
                    { zh: '保险杠', en: 'Bumper' },
                ],
            },
            {
                id: 'tires-wheels',
                label: '轮胎轮毂',
                en: 'Tires & Wheels',
                aliases: ['轮胎', '轮圈', '车轮', '胎圈'],
                seeds: [
                    { zh: '半钢轮胎', en: 'Passenger Car Tire' },
                    { zh: '全钢轮胎', en: 'Truck Tire' },
                    { zh: '工程胎', en: 'Off-The-Road Tire' },
                    { zh: '摩托车胎', en: 'Motorcycle Tire' },
                    { zh: '内胎', en: 'Inner Tube' },
                    { zh: '钢制轮毂', en: 'Steel Wheel Rim' },
                    { zh: '铝合金轮毂', en: 'Aluminum Alloy Wheel' },
                    { zh: '轮胎垫带', en: 'Tire Flap' },
                ],
            },
            {
                id: 'rail-marine',
                label: '轨道船舶',
                en: 'Rail & Marine',
                aliases: ['轨道交通', '船舶', '铁路装备', '船用设备'],
                seeds: [
                    { zh: '铁路货车', en: 'Railway Freight Wagon' },
                    { zh: '集装箱平车', en: 'Container Flat Wagon' },
                    { zh: '轨道客车', en: 'Railway Passenger Coach' },
                    { zh: '船用发动机', en: 'Marine Engine' },
                    { zh: '船用螺旋桨', en: 'Marine Propeller' },
                    { zh: '锚链', en: 'Anchor Chain' },
                    { zh: '救生艇', en: 'Lifeboat' },
                    { zh: '舷外机', en: 'Outboard Motor' },
                ],
            },
        ],
    },
    {
        id: 'agriculture-food',
        label: '农业与食品',
        en: 'Agriculture, Food & Beverage',
        theme: 'meridian',
        palette: brand_palette_1.BRAND_PALETTES.forest.hex,
        tone: 'warm',
        segments: [
            {
                id: 'grain-oil',
                label: '粮油作物',
                en: 'Grains & Oilseeds',
                aliases: ['粮食作物', '谷物油料', '原粮', '农产品原料'],
                seeds: [
                    { zh: '黄玉米', en: 'Yellow Corn' },
                    { zh: '粳稻谷', en: 'Japonica Paddy Rice' },
                    { zh: '小麦', en: 'Milling Wheat' },
                    { zh: '大豆', en: 'Soybeans' },
                    { zh: '花生仁', en: 'Peanut Kernels' },
                    { zh: '油菜籽', en: 'Rapeseed' },
                    { zh: '高粱', en: 'Sorghum' },
                    { zh: '荞麦', en: 'Buckwheat' },
                ],
            },
            {
                id: 'fresh-produce',
                label: '生鲜果蔬',
                en: 'Fresh Produce',
                aliases: ['新鲜蔬菜', '水果蔬菜', '鲜货', '农产品生鲜'],
                seeds: [
                    { zh: '大蒜', en: 'Fresh Garlic' },
                    { zh: '生姜', en: 'Fresh Ginger' },
                    { zh: '红富士苹果', en: 'Fuji Apples' },
                    { zh: '鲜香菇', en: 'Fresh Shiitake Mushrooms' },
                    { zh: '西兰花', en: 'Fresh Broccoli' },
                    { zh: '胡萝卜', en: 'Fresh Carrots' },
                    { zh: '蜜柚', en: 'Honey Pomelo' },
                    { zh: '鲜辣椒', en: 'Fresh Chili Peppers' },
                ],
            },
            {
                id: 'dried-nuts',
                label: '干果坚果',
                en: 'Dried Fruits & Nuts',
                aliases: ['坚果炒货', '干果类', '果干', '炒货'],
                seeds: [
                    { zh: '核桃仁', en: 'Walnut Kernels' },
                    { zh: '去壳花生', en: 'Blanched Peanuts' },
                    { zh: '红枣', en: 'Dried Red Dates' },
                    { zh: '葡萄干', en: 'Raisins' },
                    { zh: '枸杞干', en: 'Dried Goji Berries' },
                    { zh: '南瓜子', en: 'Pumpkin Seeds' },
                    { zh: '葵花籽', en: 'Sunflower Seeds' },
                    { zh: '柿饼', en: 'Dried Persimmons' },
                ],
            },
            {
                id: 'spice-seasoning',
                label: '调味香料',
                en: 'Spices & Seasonings',
                aliases: ['香辛料', '调味料', '调料', '香料'],
                seeds: [
                    { zh: '八角', en: 'Star Anise' },
                    { zh: '花椒', en: 'Sichuan Peppercorns' },
                    { zh: '桂皮', en: 'Cassia Bark' },
                    { zh: '白胡椒', en: 'White Pepper' },
                    { zh: '干辣椒', en: 'Dried Chili Peppers' },
                    { zh: '孜然', en: 'Cumin Seeds' },
                    { zh: '姜黄粉', en: 'Turmeric Powder' },
                    { zh: '五香粉', en: 'Five Spice Powder' },
                ],
            },
            {
                id: 'processed-food',
                label: '加工食品',
                en: 'Processed Foods',
                aliases: ['食品加工', '预包装食品', '罐头食品', '速食品'],
                seeds: [
                    { zh: '番茄酱罐头', en: 'Canned Tomato Paste' },
                    { zh: '蘑菇罐头', en: 'Canned Mushrooms' },
                    { zh: '速冻水饺', en: 'Frozen Dumplings' },
                    { zh: '挂面', en: 'Dried Noodles' },
                    { zh: '红薯粉丝', en: 'Sweet Potato Vermicelli' },
                    { zh: '蜂蜜', en: 'Natural Honey' },
                    { zh: '酱油', en: 'Soy Sauce' },
                    { zh: '米醋', en: 'Rice Vinegar' },
                ],
            },
            {
                id: 'beverage-tea',
                label: '茶饮冲调',
                en: 'Tea & Beverages',
                aliases: ['茶叶', '饮品', '冲调饮品', '咖啡茶饮'],
                seeds: [
                    { zh: '绿茶', en: 'Green Tea' },
                    { zh: '红茶', en: 'Black Tea' },
                    { zh: '乌龙茶', en: 'Oolong Tea' },
                    { zh: '茉莉花茶', en: 'Jasmine Tea' },
                    { zh: '速溶咖啡', en: 'Instant Coffee' },
                    { zh: '菊花茶', en: 'Chrysanthemum Tea' },
                    { zh: '果汁浓缩液', en: 'Fruit Juice Concentrate' },
                    { zh: '瓶装饮用水', en: 'Bottled Drinking Water' },
                ],
            },
        ],
    },
    {
        id: 'raw-materials',
        label: '原材料',
        en: 'Raw Materials',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.stone.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'metal-materials',
                label: '金属材料',
                en: 'Metal Materials',
                aliases: ['钢材', '有色金属', '金属原料', '黑色金属'],
                seeds: [
                    { zh: '热轧卷板', en: 'Hot Rolled Steel Coil' },
                    { zh: '冷轧钢板', en: 'Cold Rolled Steel Sheet' },
                    { zh: '螺纹钢', en: 'Deformed Steel Bar' },
                    { zh: '铝锭', en: 'Aluminum Ingot' },
                    { zh: '铜杆', en: 'Copper Rod' },
                    { zh: '不锈钢管', en: 'Stainless Steel Pipe' },
                    { zh: '镀锌钢丝', en: 'Galvanized Steel Wire' },
                    { zh: '生铁块', en: 'Pig Iron' },
                ],
            },
            {
                id: 'plastic-resin',
                label: '塑料原料',
                en: 'Plastic Resins',
                aliases: ['塑胶原料', '树脂', '高分子材料', '塑料粒子'],
                seeds: [
                    { zh: '聚乙烯', en: 'Polyethylene Resin' },
                    { zh: '聚丙烯', en: 'Polypropylene Resin' },
                    { zh: '聚氯乙烯', en: 'PVC Resin' },
                    { zh: 'ABS树脂', en: 'ABS Resin' },
                    { zh: '聚碳酸酯', en: 'Polycarbonate Resin' },
                    { zh: '尼龙切片', en: 'Nylon Chips' },
                    { zh: 'PET切片', en: 'PET Chips' },
                    { zh: '色母粒', en: 'Color Masterbatch' },
                ],
            },
            {
                id: 'textile-fabric',
                label: '纺织面料',
                en: 'Textile Fabrics',
                aliases: ['布料', '坯布', '面料', '纺织原料'],
                seeds: [
                    { zh: '涤纶牛津布', en: 'Polyester Oxford Fabric' },
                    { zh: '全棉坯布', en: 'Cotton Greige Fabric' },
                    { zh: '涤棉混纺布', en: 'Poly Cotton Blended Fabric' },
                    { zh: '针织汗布', en: 'Knitted Jersey Fabric' },
                    { zh: '无纺布', en: 'Nonwoven Fabric' },
                    { zh: '牛仔布', en: 'Denim Fabric' },
                    { zh: '涤纶长丝', en: 'Polyester Filament Yarn' },
                    { zh: '棉纱', en: 'Cotton Yarn' },
                ],
            },
            {
                id: 'chemical-raw',
                label: '化工原料',
                en: 'Chemical Raw Materials',
                aliases: ['化工品', '基础化工', '化学原料', '化工材料'],
                seeds: [
                    { zh: '纯碱', en: 'Soda Ash' },
                    { zh: '烧碱', en: 'Caustic Soda' },
                    { zh: '钛白粉', en: 'Titanium Dioxide' },
                    { zh: '环氧树脂', en: 'Epoxy Resin' },
                    { zh: '硫酸', en: 'Sulfuric Acid' },
                    { zh: '碳酸钙', en: 'Calcium Carbonate' },
                    { zh: '工业酒精', en: 'Industrial Alcohol' },
                    { zh: '增塑剂', en: 'Plasticizer' },
                ],
            },
            {
                id: 'building-materials',
                label: '建筑材料',
                en: 'Building Materials',
                aliases: ['建材', '建筑原料', '土建材料', '工程材料'],
                seeds: [
                    { zh: '水泥', en: 'Portland Cement' },
                    { zh: '平板玻璃', en: 'Flat Glass' },
                    { zh: '建筑陶瓷', en: 'Ceramic Tiles' },
                    { zh: '石膏板', en: 'Gypsum Board' },
                    { zh: '加气砖', en: 'AAC Block' },
                    { zh: '防水卷材', en: 'Waterproof Membrane' },
                    { zh: '石英砂', en: 'Quartz Sand' },
                    { zh: '岩棉板', en: 'Rock Wool Board' },
                ],
            },
            {
                id: 'wood-pulp',
                label: '木材纸浆',
                en: 'Wood and Pulp',
                aliases: ['木料', '原木', '纸浆', '板材'],
                seeds: [
                    { zh: '原木', en: 'Log Timber' },
                    { zh: '胶合板', en: 'Plywood Board' },
                    { zh: '中密度纤维板', en: 'MDF Board' },
                    { zh: '刨花板', en: 'Particle Board' },
                    { zh: '木方', en: 'Lumber Beam' },
                    { zh: '竹胶板', en: 'Bamboo Plywood' },
                    { zh: '木浆', en: 'Wood Pulp' },
                    { zh: '单板', en: 'Wood Veneer' },
                ],
            },
        ],
    },
    {
        id: 'fabrication-services',
        label: '来图代工',
        en: 'Fabrication Services',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.steel.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'metal-fabrication',
                label: '五金加工',
                en: 'Metal Fabrication',
                aliases: ['钣金加工', '金属加工', '机加工', '冲压件'],
                seeds: [
                    { zh: '钣金机箱', en: 'Sheet Metal Enclosure' },
                    { zh: '冲压件', en: 'Stamped Metal Part' },
                    { zh: '焊接支架', en: 'Welded Steel Bracket' },
                    { zh: 'CNC车削件', en: 'CNC Turned Part' },
                    { zh: '激光切割件', en: 'Laser Cut Plate' },
                    { zh: '折弯件', en: 'Bent Metal Component' },
                    { zh: '铝型材框架', en: 'Aluminum Profile Frame' },
                    { zh: '金属冲压模具', en: 'Metal Stamping Die' },
                ],
            },
            {
                id: 'plastic-molding',
                label: '塑胶成型',
                en: 'Plastic Molding',
                aliases: ['注塑加工', '塑料件', '模具注塑', '塑胶件'],
                seeds: [
                    { zh: '注塑件', en: 'Injection Molded Part' },
                    { zh: '塑料外壳', en: 'Plastic Housing' },
                    { zh: '注塑模具', en: 'Injection Mold' },
                    { zh: '吹塑件', en: 'Blow Molded Part' },
                    { zh: '吸塑托盘', en: 'Thermoformed Tray' },
                    { zh: '挤出型材', en: 'Plastic Extrusion Profile' },
                    { zh: '硅胶按键', en: 'Silicone Keypad' },
                    { zh: '滚塑水箱', en: 'Rotomolded Tank' },
                ],
            },
            {
                id: 'cnc-machining',
                label: '精密机加',
                en: 'Precision Machining',
                aliases: ['数控加工', 'CNC加工', '精密零件', '车铣加工'],
                seeds: [
                    { zh: 'CNC铣削件', en: 'CNC Milled Part' },
                    { zh: '精密轴套', en: 'Precision Bushing' },
                    { zh: '齿轮坯', en: 'Gear Blank' },
                    { zh: '铝合金端盖', en: 'Aluminum End Cap' },
                    { zh: '不锈钢法兰', en: 'Stainless Steel Flange' },
                    { zh: '螺纹接头', en: 'Threaded Fitting' },
                    { zh: '精密导轨', en: 'Precision Guide Rail' },
                    { zh: '五轴加工件', en: 'Five Axis Machined Part' },
                ],
            },
            {
                id: 'sheet-metal',
                label: '钣金结构',
                en: 'Sheet Metal Works',
                aliases: ['钣金件', '机箱机柜', '金属结构件', '钣金外壳'],
                seeds: [
                    { zh: '机柜', en: 'Equipment Cabinet' },
                    { zh: '控制箱', en: 'Control Box' },
                    { zh: '配电柜壳体', en: 'Distribution Panel Enclosure' },
                    { zh: '设备护罩', en: 'Machine Guard Cover' },
                    { zh: '金属支架', en: 'Metal Mounting Bracket' },
                    { zh: '不锈钢台面', en: 'Stainless Steel Countertop' },
                    { zh: '通风管道', en: 'Ventilation Duct' },
                    { zh: '金属网罩', en: 'Metal Mesh Guard' },
                ],
            },
            {
                id: 'surface-treatment',
                label: '表面处理',
                en: 'Surface Treatment',
                aliases: ['喷涂加工', '电镀加工', '表面喷涂', '氧化处理'],
                seeds: [
                    { zh: '粉末喷涂件', en: 'Powder Coated Part' },
                    { zh: '阳极氧化件', en: 'Anodized Aluminum Part' },
                    { zh: '电镀锌件', en: 'Zinc Plated Part' },
                    { zh: '喷砂处理件', en: 'Sandblasted Component' },
                    { zh: '拉丝面板', en: 'Brushed Metal Panel' },
                    { zh: '烤漆外壳', en: 'Baked Enamel Shell' },
                    { zh: '钝化处理件', en: 'Passivated Part' },
                    { zh: '丝印标牌', en: 'Screen Printed Label' },
                ],
            },
            {
                id: 'assembly-oem',
                label: '组装代工',
                en: 'Assembly Services',
                aliases: ['整机组装', 'OEM组装', '成品组装', '来图代工'],
                seeds: [
                    { zh: '整机装配', en: 'Finished Machine Assembly' },
                    { zh: '线束组件', en: 'Wire Harness Assembly' },
                    { zh: '电子控制盒', en: 'Electronic Control Box' },
                    { zh: '机械模组', en: 'Mechanical Module' },
                    { zh: '包装成品', en: 'Packaged Finished Product' },
                    { zh: '焊接组件', en: 'Welded Assembly' },
                    { zh: '传动组件', en: 'Drive Assembly' },
                    { zh: '定制夹具', en: 'Custom Fixture' },
                ],
            },
        ],
    },
    {
        id: 'business-services',
        label: '企业服务',
        en: 'Service',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.slate.hex,
        tone: 'minimal',
        segments: [
            {
                id: 'freight-forwarding',
                label: '国际货运',
                en: 'Freight Forwarding',
                aliases: ['货代', '海运代理', '空运代理', '物流运输', '国际物流'],
                seeds: [
                    { zh: '海运整柜', en: 'Full Container Load Ocean Freight' },
                    { zh: '海运拼箱', en: 'Less Than Container Load Ocean Freight' },
                    { zh: '空运包板', en: 'Air Freight Consolidation' },
                    { zh: '中欧班列', en: 'China-Europe Rail Freight' },
                    { zh: '国际快递', en: 'International Express Delivery' },
                    { zh: '危险品运输', en: 'Dangerous Goods Shipping' },
                    { zh: '冷链运输', en: 'Refrigerated Cargo Shipping' },
                    { zh: '门到门配送', en: 'Door to Door Delivery' },
                ],
            },
            {
                id: 'customs-brokerage',
                label: '报关清关',
                en: 'Customs Brokerage',
                aliases: ['报关行', '清关代理', '通关服务', '报关代理', '进出口报关'],
                seeds: [
                    { zh: '出口报关', en: 'Export Customs Declaration' },
                    { zh: '进口清关', en: 'Import Customs Clearance' },
                    { zh: '原产地证', en: 'Certificate of Origin Handling' },
                    { zh: '关税筹划', en: 'Duty Optimization Service' },
                    { zh: '保税仓储', en: 'Bonded Warehouse Clearance' },
                    { zh: '转关运输', en: 'Customs Transshipment' },
                    { zh: '退运报关', en: 'Return Cargo Declaration' },
                    { zh: 'AEO认证辅导', en: 'AEO Certification Consulting' },
                ],
            },
            {
                id: 'inspection-certification',
                label: '检验认证',
                en: 'Inspection and Certification',
                aliases: ['验货', '质检', '认证服务', '第三方检测', '产品认证'],
                seeds: [
                    { zh: '出货前验货', en: 'Pre-Shipment Inspection' },
                    { zh: '工厂审核', en: 'Factory Audit' },
                    { zh: '产品测试', en: 'Product Testing Service' },
                    { zh: 'CE认证', en: 'CE Certification Service' },
                    { zh: 'FDA注册', en: 'FDA Registration Service' },
                    { zh: 'ISO体系认证', en: 'ISO System Certification' },
                    { zh: '装柜监装', en: 'Loading Supervision' },
                    { zh: '样品检验', en: 'Sample Inspection' },
                ],
            },
            {
                id: 'warehousing-fulfillment',
                label: '仓储配送',
                en: 'Warehousing and Fulfillment',
                aliases: ['海外仓', '仓储服务', '集货仓', '分拣配送', '仓配一体'],
                seeds: [
                    { zh: '海外仓', en: 'Overseas Warehouse Service' },
                    { zh: '保税仓', en: 'Bonded Warehousing' },
                    { zh: '集货拼柜', en: 'Cargo Consolidation' },
                    { zh: '贴标换标', en: 'Labeling and Relabeling' },
                    { zh: '一件代发', en: 'Dropshipping Fulfillment' },
                    { zh: '退货处理', en: 'Returns Processing' },
                    { zh: '库存管理', en: 'Inventory Management' },
                    { zh: '尾程派送', en: 'Last Mile Delivery' },
                ],
            },
            {
                id: 'trade-documentation',
                label: '外贸单证',
                en: 'Trade Documentation',
                aliases: ['单证服务', '外贸跟单', '制单', '信用证', '贸易文件'],
                seeds: [
                    { zh: '商业发票', en: 'Commercial Invoice Preparation' },
                    { zh: '装箱单', en: 'Packing List Preparation' },
                    { zh: '提单', en: 'Bill of Lading Handling' },
                    { zh: '信用证审单', en: 'Letter of Credit Checking' },
                    { zh: '原产地证申领', en: 'Certificate of Origin Application' },
                    { zh: '报关单证', en: 'Customs Documentation' },
                    { zh: '领事认证', en: 'Consular Legalization' },
                    { zh: '外贸合同', en: 'Export Contract Drafting' },
                ],
            },
            {
                id: 'after-sales-repair',
                label: '售后维修',
                en: 'After-Sales Repair',
                aliases: ['海外售后', '返修服务', '维修中心', '退换货处理', '售后支持'],
                seeds: [
                    { zh: '海外维修', en: 'Overseas Repair Service' },
                    { zh: '返修翻新', en: 'Refurbishment Service' },
                    { zh: '备件供应', en: 'Spare Parts Supply' },
                    { zh: '退换货处理', en: 'Return and Exchange Handling' },
                    { zh: '上门安装', en: 'On-Site Installation' },
                    { zh: '技术培训', en: 'Technical Training' },
                    { zh: '保修服务', en: 'Warranty Service' },
                    { zh: '远程技术支持', en: 'Remote Technical Support' },
                ],
            },
        ],
    },
];
function industryOptions() {
    return exports.TAXONOMY.map((item) => ({ id: item.id, label: item.label }));
}
function findIndustry(id) {
    if (!id)
        return null;
    return exports.TAXONOMY.find((item) => item.id === id) ?? null;
}
function findSegment(industryId, segmentId) {
    if (!segmentId)
        return null;
    const industry = findIndustry(industryId);
    return industry?.segments.find((item) => item.id === segmentId) ?? null;
}
function isValidIndustry(id) {
    return exports.TAXONOMY.some((item) => item.id === id);
}
/** `segment` 必须属于这个 `industry` —— 只验它自身存在是不够的 */
function isValidSegment(industryId, segmentId) {
    return Boolean(findSegment(industryId, segmentId));
}
/**
 * 行业 → 版式。
 *
 * 认不出的一律退回 `aurora`：**不要抛错**。这个函数的调用点在建站事务里，
 * 抛出去等于"客户填了个我们没见过的行业词，站建不出来"，
 * 而它其实只是一套配色没定而已。
 */
function pickTheme(industryId) {
    return findIndustry(industryId)?.theme ?? 'aurora';
}
/**
 * 按客户手写的一句话归类到已有的二级品类。
 *
 * 只做**别名精确/包含**匹配，不调模型 —— 模型归类放在 provider 里，
 * 那是另一条路（`segmentFallback`）。这里负责把"手动工具""手工具"这类
 * 明显是同一件事的词直接命中，省一次调用、也不会归错。
 */
function matchSegmentByText(industryId, text) {
    const industry = findIndustry(industryId);
    if (!industry)
        return null;
    const needle = text.trim().toLowerCase();
    if (!needle)
        return null;
    for (const segment of industry.segments) {
        const words = [segment.label, segment.en, ...(segment.aliases ?? [])];
        for (const word of words) {
            const hay = word.toLowerCase();
            // 双向包含：客户写"手动工具类"，别名是"手动工具"（别名被包含）；
            // 客户写"工具"，label 是"五金工具"（label 被包含）
            if (needle === hay || needle.includes(hay) || hay.includes(needle)) {
                return segment;
            }
        }
    }
    return null;
}
/** 一个二级品类的英文名提示，拼进提示词。没有种子时给空串 */
function segmentSeedsEn(segment) {
    return segment ? segment.seeds.map((item) => item.en).join(', ') : '';
}
/** 追问时当 chip 用的中文建议，取前 `count` 条 */
function segmentHintsZh(segment, count = 5) {
    return segment ? segment.seeds.slice(0, count).map((item) => item.zh) : [];
}
//# sourceMappingURL=taxonomy.js.map